import {GraphQLClient} from 'graphql-request';
import {
  _IsLink,
  _ToTitleCase,
  FILE_API,
  GRAPH_API,
  ANALYTICS_API,
  EApplicationStateType,
} from 'constants/index';
import {
  lshGETAUTH,
  lshREMOVEAUTH,
  lshSAVEAUTH,
} from 'services/LocalStorageService';
import NavigationService from 'services/NavigationService';
import {
  IDeliveryCost,
  IDeliveryLocationInput,
  IDeliveryProductInput,
  IFarmInputProductDepotInfoInput,
  IAuthResponse,
  ICompanyInput,
  ISupplierCompanyProfile,
  ICompletedSupportForm,
  ICreateQuoteResponse,
  IDepot,
  IDepotStatistics,
  IFarmer,
  IFarmInputInvoice,
  IFarmInputProduct,
  IFarmInputProductDepotInfo,
  IFarmInputProductInput,
  IFarmInputProductSubCategory,
  IFarmInputProductSubSubCategory,
  IFarmInputQuote,
  IFarmInputQuoteLineItem,
  IGenericResponse,
  IInputMessage,
  IInputQuoteAddress,
  IInputQuoteProductInput,
  IInvoiceSupportingDocumentInput,
  IOrderReplacement,
  IProductPromoteInput,
  IProvince,
  IQuoteMessage,
  IQuoteSupportingDocumentInput,
  ISupplierCompanyPermissionsInput,
  ISupplierUserInput,
  ISupplierUserPermission,
  ISupplierUserProfile,
  ISupportCustomer,
  ISupportingDocument,
  ISupportMessage,
  IUser,
  IFarmerSignUpInput,
  IFarmerAddressInput,
  IBug,
  ISupplierAnalytics,
  IFarmInputPurchaseOrder,
  IGenericType,
  IWalletStatement,
  IPurchaseOrderSupportingDocumentInput,
  IInvoiceBundle,
  IFarmInputCreditNote,
  IFarmInputCreditNoteLineItemInput,
  IFarmInputCreditNoteLineItem,
  IFarmInputProgram,
  IProduce,
  ISoilSampling,
  IFarmInputProgramInput,
  IFdApplicationType,
  IFdAgreementDocType,
  IFdApplicationAgreementType,
  IFdUser,
  IFdCompany,
  ITraderApplication,
  ITradingForm,
  IFdCrop,
  IFdFarm,
  IUpdateFdFarm,
  ISaveFdProductionPlanDto,
  IFdProductionPlan,
  ISaveFdProductionHistory,
  IFdProductionHistory,
  ISaveFdFinancialPerformance,
  IFdFinancialPerformance,
  IFdApplicationDocType,
  ISaveFdDoc,
  ILayerResponse,
  ILayerHistoryResponse,
  IPolygonData,
  ILinkedGpayAccount,
  EGPAY_ACCOUNT_TYPE,
  IGpayAccount,
  IPolygonInput,
  ISaveFdOperatingCost,
  IFdOperatingCost,
  IFdFarmAssetList,
  ISaveFdFarmAsset,
  IFdLiability,
  ISaveLiability,
  IFdProductionCost,
  ISaveProductionCost,
  IAiBusinessPlan,
  IFarmTeam,
  ICustomerSupplier,
  IFarmRiskBasic,
  IFarmRiskByApp,
  ICreateFarmRiskDto,
  IRunItcTraderApplicationDto,
  IitcDoc,
  ICompanyItcDoc,
  IPerformCompanyCreditCheckDto,
  IFdFundingAsk,
  IFdFundingRequestCost,
  IWidget,
  IWidgetInput,
  IApplicationOwnershipType,
  ICreateApplicationOwnershipDto,
  IFarmFieldLogData,
  IWalletRestriction,
} from 'constants/interfaces';
import {reduxClearAll, reduxSetUser} from 'appredux/actions';
import SocketService from 'services/SocketService';
import {STORAGE_ACTION} from 'services/LocalStorageService';

export const graphqlRequestHandler = (
  name: any,
  query: any,
  variables: any,
  headers = {},
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const client = new GraphQLClient(GRAPH_API, {
      headers: {
        ...headers,
        platform: 'WEB_SUPPLIER',
        requestSourceUserType: 'supplier',
      },
    });
    client
      .request(query, variables)
      .then((data: any) => {
        resolve(name ? data[name] : data);
      })
      .catch((error: any) => {
        let shouldReject = true;
        if (error.response) {
          (error.response.errors ?? []).forEach((item: any) => {
            const tokenErrorMessages = ['not authenticate'];
            if (tokenErrorMessages.includes(item.message.toLocaleLowerCase())) {
              shouldReject = true;
              lshREMOVEAUTH();
              reduxClearAll();
              NavigationService.navigate('/token-expired');
            }
          });
        }
        if (shouldReject) {
          reject(
            error.response
              ? error.response.errors
                ? error.response.errors.map((i: any) => i.message)
                : error.response
              : error,
          );
        }
      });
  });
};

/**
 * Api to log in a user to platform
 * @param {string} email
 * @param {string} password
 * @return {Promise}
 */
export async function apiLOGIN(
  email: string,
  password: string,
): Promise<IGenericResponse> {
  return new Promise((resolve, reject) => {
    const query = `
                    query variables($email:String!,$password:String!){
                        farmInputSupplierLogin(email:$email,password:$password){
                            token
                            success
                            message
                            state
                            verified
                        }
                    }`;
    const variables = {email, password};
    graphqlRequestHandler('farmInputSupplierLogin', query, variables)
      .then((auth: IAuthResponse) => {
        if (auth.success) {
          auth?.token && SocketService.setOnline(auth?.token);
          lshSAVEAUTH(auth).then((d) => {
            if (d.success) {
              apiSAVEMYPROFILE().then((d) => {
                resolve(d);
              });
            } else {
              const error = new Error(d.message);
              reject(error);
            }
          });
        } else {
          resolve(auth);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const apiGetNDVI = async (
  startDate: string,
  endDate: string,
  polygons?: IPolygonData[],
): Promise<ILayerResponse[]> => {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const query = `
    query variables ($polygons: [PolygonInput!]!, $startDate: String!, $endDate: String!) {
      getNDVI(polygons: $polygons, endDate: $endDate, startDate: $startDate) {
        url
        error
       
      }
    } 
  `;
    const variables = {startDate, endDate, polygons};
    return new Promise(async (resolve, reject) => {
      graphqlRequestHandler('getNDVI', query, variables, headers)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
};

export const apiFieldLayers = async (
  startDate: string,
  endDate: string,
  polygons?: IPolygonData[],
): Promise<ILayerResponse[][]> => {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const query = `
    query variables ($polygons: [PolygonInput!]!, $startDate: String!, $endDate: String!) {
      getFieldLayers(polygons: $polygons, endDate: $endDate, startDate: $startDate) {
        url
        error
         riskLevel
        layer
      }
    } 
  `;
    const variables = {startDate, endDate, polygons};
    return new Promise(async (resolve, reject) => {
      graphqlRequestHandler('getFieldLayers', query, variables, headers)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
};

export const apiFieldLayersByIds = async (
  fieldIds: Array<string | number>,
  analysisDate: string,
): Promise<ILayerHistoryResponse[]> => {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const query = `
    query variables($fieldIds: [ID!]!, $analysisDate: String!) {
      getFieldLayersByIDs(fieldIds: $fieldIds, analysisDate: $analysisDate) {
        fieldId
        analysisDate
        layers {
          url
          error
          riskLevel
          layer
        }
      }
    }
  `;
    const variables = {fieldIds, analysisDate};
    return new Promise(async (resolve, reject) => {
      graphqlRequestHandler('getFieldLayersByIDs', query, variables, headers)
        .then((res) => resolve((res as ILayerHistoryResponse[]) || []))
        .catch((error) => reject(error));
    });
  });
};

export async function apiGetFarmerSummary(
  crmUserId: number,
): Promise<import('./interfaces').IFdFarmerSummary> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {crmUserId};
    return new Promise((resolve, reject) => {
      const query = `
        query getFarmerSummary($crmUserId: Int!) {
          getFarmerSummary(crmUserId: $crmUserId) {
            totalFarmerCount
            avgFarmSizeHectares
            provinceBreakdown {
              province
              farmerCount
            }
            cropBreakdown {
              crop
              farmerCount
            }
          }
        }`;
      graphqlRequestHandler('getFarmerSummary', query, variables, headers)
        .then((res) => resolve(res as import('./interfaces').IFdFarmerSummary))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetProgramSummary(): Promise<
  import('./interfaces').IProgramSummary
> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    return new Promise((resolve, reject) => {
      const query = `
        query getProgramSummary {
          getProgramSummary {
            totalCount
            totalValue
            typeBreakdown {
              type
              count
            }
            statusBreakdown {
              status
              count
            }
            cropBreakdown {
              crop
              count
            }
          }
        }`;
      graphqlRequestHandler('getProgramSummary', query, {}, headers)
        .then((res) => resolve(res as import('./interfaces').IProgramSummary))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetQuoteDashboard(): Promise<{
  summary: import('./interfaces').IQuoteSummary;
  conversion: import('./interfaces').IQuoteConversionRate;
}> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    return new Promise((resolve, reject) => {
      const query = `
        query getQuoteDashboard {
          getQuoteSummary {
            statusCounts {
              state
              label
              count
            }
            depotBreakdown {
              depotId
              depotName
              count
            }
            valueTimeline {
              date
              totalValue
            }
            needsAttention {
              id
              quoteNumber
              total
              farmer {
                id
                name
                surname
              }
              insertedDate
            }
          }
          getQuoteConversionRate {
            totalQuotes
            acceptedQuotes
            rejectedQuotes
            conversionRate
          }
        }`;
      graphqlRequestHandler(null, query, {}, headers)
        .then((res: any) =>
          resolve({
            summary: res.getQuoteSummary,
            conversion: res.getQuoteConversionRate,
          }),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFavouriteProducts(
  limit: number = 10,
): Promise<import('./interfaces').IFavouriteProduct[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    return new Promise((resolve, reject) => {
      // NOTE: brandSupplierId is not strictly needed for frontend as the backend scopes via JWT. We pass empty or '0'.
      const query = `
        query getFavouriteProducts {
          getFavouriteProducts(brandSupplierId: "0", limit: ${limit}) {
            productId
            stockCode
            productName
            currentPrice
            previousPrice
            priceChangedAt
            favouriteCount
          }
        }`;
      graphqlRequestHandler('getFavouriteProducts', query, {}, headers)
        .then((res: any) => resolve(res.getFavouriteProducts || []))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGenerateAiProgram(
  soilSampleData: ISoilSampling,
): Promise<any> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const query = `
  query variables($soilData: aiProgramInput!) {
      generateSoilSampling(soilData: $soilData) {
              applicationStage
              week
              product {
              id
              name
              price
              image
              }
              applicationStyle
              applicationRate
              notes
          }
  }


`;

    const variables = {
      soilData: soilSampleData,
    };
    console.log('var', variables);

    return new Promise(async (resolve, reject) => {
      graphqlRequestHandler('generateSoilSampling', query, variables, headers)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetProgramProducts(): Promise<IFarmInputProduct[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const query = `
          query{
              farmInputProducts{
                  id
                  name
                  price
                  category
                  stockCode
                  image
                  size
                  measurement
                  productType
                  activeIngredient
                  tradeName
                  labelLink
                  mainCategoryId
                          subCategoryId
                  subCategoryName
                  isActive
                  grade
                  promotedPrice
              } 
          }`;
      const variables = {};
      graphqlRequestHandler('farmInputProducts', query, variables, headers)
        .then((products: IFarmInputProduct[]) => resolve(products))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGenerateKhulaProgram(
  inputData?: IFarmInputProgramInput,
): Promise<any> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const lines = inputData?.lines || [];
    const vars = {
      id: inputData?.id ? Number(inputData.id) : undefined,
      active: true,
      farmerId: inputData?.farmerId,
      produceId: inputData?.produceId,
      type: inputData?.type,
      status: 'Submitted',
      hectares: inputData?.hectares,
      lines: lines.map((line) => ({
        id: line.id ? Number(line.id) : undefined,
        productId: line.productId,
        when: line.when,
        quantity: line.quantity,
        PHI: line.PHI || 0,
        applicationRate: line.applicationRate,
        applicationUnit: line.applicationUnit,
        applicationType: line.applicationType,
        applicationTypePerHectare: line.applicationTypePerHectare,
        comment: line.comment,
        price: line.price,
      })),
    };
    const query = `
        mutation variables($input: FarmInputProgramInput!) {
              farmInputProgramSave(input: $input) {
                id
                createdDate
                active
                farmer {
                  id
                }
                produce {
                  id
                }
                type
                status
                lines {
                  id
                  programId
                  product {
                    id
                    name
                    price
                  }
                  price
                  when
                  quantity
                  PHI
                  applicationRate
                  applicationUnit
                  applicationType
                  applicationTypePerHectare
                  comment
                }
              }
        }
`;

    const variables = {
      input: vars,
    };
    console.log('var', variables);

    return new Promise(async (resolve, reject) => {
      graphqlRequestHandler('farmInputProgramSave', query, variables, headers)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

/**
 * Api to save user's profile in redux
 * @return {Promise}
 */
export async function apiSAVEMYPROFILE(): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      // todo deprecate depot field
      const query = `
                query {
                    farmInputSupplier{
                        id
                        name
                        surname
                        email
                        phone
                        pictureUrl
                        address
                        latlong
                        isActive
                        invitationCode
                        isAdmin
                        isKhulaAdmin
                        userId
                        isCommodityGroup
                        depot {
                            id
                            nulandisId
                            code
                            name
                            isActive
                            contact
                            phone
                            address1
                            address2
                            address3
                            address4
                            address5
                            latitude
                            longitude
                        }
                        depots {
                            id
                            nulandisId
                            code
                            name
                            isActive
                            contact
                            phone
                            address1
                            address2
                            address3
                            address4
                            address5
                            latitude
                            longitude
                        }
                        company{
                            id
                            name
                            image
                            location
                            addressLine1
                            addressLine2
                            city
                            province
                            areaCode
                            depotPriceList
                            gpayEntityId
                            bankId
                            bankAccountTypeId
                            bankAccountNumber
                            bankBranchCode
                        }
                        brand{
                            id
                            name
                            image
                        }
                        mainSupplier{
                            id
                            name
                            image
                            marginPercentage
                        }
                        companyPermissions{
                            customers
                            customerApplications
                            customerWallet
                            customerFieldsAndLayers
                            customerPrograms
                            products
                            productsGlobalViewOnly
                            quotes
                            orders
                            fundingApplications
                            fundingApplicationsViewOnly
                            programs
                            programsViewOnly
                        }
                        permissions{
                            id
                            name
                        }
                    }
                }`;
      const variables = {};
      graphqlRequestHandler('farmInputSupplier', query, variables, headers)
        .then((user: ISupplierUserProfile) => {
          user.isAECI = user.mainSupplier?.id === '1';
          // If we have previously stored pending company permissions for this
          // invited admin (fallback from the onboarding flow), merge them into
          // the freshly fetched user profile before storing in redux. This
          // ensures newly-created companies show the selected permissions on
          // first login even if the backend hasn't persisted them yet.
          try {
            const emailKey = `pendingPermissions:${user?.email || ''}`;
            const pending = STORAGE_ACTION._retrieveData(emailKey);
            console.log(
              'Retrieved pending permissions for',
              user?.email,
              ':',
              pending,
            );
            if (
              pending &&
              typeof pending === 'object' &&
              pending.companyPermissions
            ) {
              console.log(
                'Merging pending permissions:',
                pending.companyPermissions,
              );
              console.log(
                'Current companyPermissions:',
                user.companyPermissions,
              );
              user.companyPermissions = {
                ...(user.companyPermissions || {}),
                ...(pending.companyPermissions || {}),
              };
              console.log(
                'Merged companyPermissions:',
                user.companyPermissions,
              );
              // best-effort cleanup
              try {
                STORAGE_ACTION._removeData(emailKey);
              } catch (e) {
                // ignore
              }
            } else {
              console.log(
                'No pending permissions found or backend already persisted them',
              );
            }
          } catch (err) {
            console.warn('Failed to merge pending permissions', err);
          }
          if (user) {
            reduxSetUser(user);
            resolve({success: true});
          } else {
            const error = new Error('no user data');
            reject(error);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiSendResetPasswordEmail(
  email: string,
): Promise<IGenericResponse> {
  return new Promise((resolve, reject) => {
    const variables = {username: email};
    const query = `
        query variables($username: String!){
            forgotPassword(username: $username){
                success
                message
            }
        }`;
    graphqlRequestHandler('forgotPassword', query, variables, {})
      .then((response: IGenericResponse) => resolve(response))
      .catch((error) => reject(error));
  });
}

export async function apiResetPassword(
  email: string,
  resetPasswordToken: string,
  newPassword: string,
): Promise<IGenericResponse> {
  return new Promise((resolve, reject) => {
    const variables = {
      username: email,
      token: resetPasswordToken,
      newPassword: newPassword,
    };
    const query = `
        mutation variables($username: String!, $newPassword: String!, $token: String!){
            resetPassword(username: $username, newPassword: $newPassword, token: $token){
                success
                message
            }
        }`;
    graphqlRequestHandler('resetPassword', query, variables, {})
      .then((response: IGenericResponse) => resolve(response))
      .catch((error) => reject(error));
  });
}

/**
 * Api to get user's team and save in redux
 * @return {Promise}
 */
export async function apiGetTeam(): Promise<ISupplierUserProfile[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    // todo deprecate depot field
    return new Promise((resolve, reject) => {
      const query = `
                query {
                    farmInputSuppliersMyTeam{
                        id
                        name
                        surname
                        email
                        phone
                        pictureUrl
                        address
                        latlong
                        isActive
                        isAdmin
                        userId
                        depot{
                            id
                            nulandisId
                            code
                            name
                            isActive
                            contact
                            phone
                            address1
                            address2
                            address3
                            address4
                            address5
                            latitude
                            longitude
                        }
                        depots{
                            id
                            nulandisId
                            code
                            name
                            isActive
                            contact
                            phone
                            address1
                            address2
                            address3
                            address4
                            address5
                            latitude
                            longitude
                        }
                        company{
                            id
                            name
                            image
                            location
                            addressLine1
                            addressLine2
                            city
                            province
                            areaCode
                        }
                        brand{
                            id
                            name
                            image
                        }
                    } 
                }`;
      const variables = {};
      graphqlRequestHandler(
        'farmInputSuppliersMyTeam',
        query,
        variables,
        headers,
      )
        .then((teamMembers: ISupplierUserProfile[]) => {
          resolve(teamMembers);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiAddTeamMember(
  teamMember: ISupplierUserInput,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($input: FarmInputSupplierInput!){
              farmInputSupplierSignUp(input: $input){
                  message
                  success
                  state
                  verified
              }
          }
      `;
      const variables = {input: teamMember};
      graphqlRequestHandler(
        'farmInputSupplierSignUp',
        query,
        variables,
        headers,
      )
        .then((response: IGenericResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateTeamMember(
  teamMember: ISupplierUserInput,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($input: FarmInputSupplierInput!){
              farmInputSupplierSave(input: $input){
                  id
              }
          }
      `;
      const variables = {input: teamMember};
      graphqlRequestHandler('farmInputSupplierSave', query, variables, headers)
        .then(() => resolve({success: true}))
        .catch((error) => reject(error));
    });
  });
}

export async function apiRemoveTeamMember(
  teamMemberId: string,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($id: ID!){
              farmInputSupplierDelete(id: $id)
          }
      `;
      const variables = {id: parseInt(teamMemberId)};
      graphqlRequestHandler(
        'farmInputSupplierDelete',
        query,
        variables,
        headers,
      )
        .then(() => resolve({success: true}))
        .catch((error) => reject(error));
    });
  });
}

export async function apiFindTeamMember(
  email: string,
): Promise<ISupplierUserProfile | undefined> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {email};
    // todo deprecate depot field
    return new Promise((resolve, reject) => {
      const query = `
          query variables($email: String!){
              farmInputSupplierByEmail(email: $email){
                  id
                  name
                  surname
                  email
                  phone
                  pictureUrl
                  address
                  latlong
                  isActive
                  isAdmin
                  userId  
                  depot{
                      id
                      nulandisId
                      code
                      name
                      isActive
                      contact
                      phone
                      address1
                      address2
                      address3
                      address4
                      address5
                      latitude
                      longitude
                  }
                  depots{
                      id
                      nulandisId
                      code
                      name
                      isActive
                      contact
                      phone
                      address1
                      address2
                      address3
                      address4
                      address5
                      latitude
                      longitude
                  }
                  company{
                      id
                      name
                      image
                      location
                      addressLine1
                      addressLine2
                      city
                      province
                      areaCode
                  }
                  brand{
                      id
                      name
                      image
                  }
              }
          }
      `;
      graphqlRequestHandler(
        'farmInputSupplierByEmail',
        query,
        variables,
        headers,
      )
        .then((supplier: ISupplierUserProfile | undefined) => resolve(supplier))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetDepotOrders(
  limit?: number,
  farmerId?: number | string,
): Promise<IFarmInputInvoice[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {limit, farmerId};
    return new Promise((resolve, reject) => {
      const query = `
                query variables($limit: Int, $farmerId: ID){
                    farmInputInvoicesForSupplier(limit: $limit, farmerId: $farmerId){
                        id
                        invoiceNumber
                        insertedDate
                        updatedDate
                        isPaid
                        latitude
                        longitude
                        address
                        total
                        outstanding
                        deliveryType
                        documentUrl
                        proformaInvoiceUrl
                        state
                        vatExempt
                        orders{
                            id
                            quantity
                            price
                            isDelivered
                            dueDate
                            logisticsType
                            logisticsTypeName
                            product{
                                id
                                name
                                category
                                stockCode
                                size
                                price
                                measurement
                                grade
                                activeIngredient
                                tradeName
                                image
                            }
                        }
                        farmer{
                            id
                            name
                            surname
                            email
                            phone
                            image
                            address
                            farm{
                                id
                                name
                                sizeHectares
                                type
                                turnover
                                numberOfEmployees
                                province
                                latLong
                            }
                        }
                        orderStatus{
                            index
                            status
                            description
                        }
                        depot{
                            name
                        }
                    } 
                }`;
      graphqlRequestHandler(
        'farmInputInvoicesForSupplier',
        query,
        variables,
        headers,
      )
        .then((invoices: IFarmInputInvoice[]) => {
          invoices.forEach((invoice) => {
            invoice.currOrderStatus =
              invoice.orderStatus.find(
                (item) => item.status === 'IN_PROGRESS',
              ) || invoice.orderStatus[0];
            if (!_IsLink(invoice.farmer?.image)) {
              invoice.farmer.image = '';
            }
          });
          resolve(invoices);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetProformaInvoicesForSupplier(
  limit?: number,
  farmerId?: number | string,
): Promise<IFarmInputInvoice[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {limit, farmerId};
    return new Promise((resolve, reject) => {
      const query = `
                query variables($limit: Int, $farmerId: ID){
                    farmInputInvoicesForSupplier(limit: $limit, farmerId: $farmerId){
                        id
                        invoiceNumber
                        insertedDate
                        updatedDate
                        isPaid
                        latitude
                        longitude
                        address
                        total
                        outstanding
                        deliveryType
                        documentUrl
                        proformaInvoiceUrl
                        state
                        vatExempt
                        orders{
                            id
                            quantity
                            price
                            isDelivered
                            dueDate
                            logisticsType
                            logisticsTypeName
                            product{
                                id
                                name
                                category
                                stockCode
                                size
                                price
                                measurement
                                grade
                                activeIngredient
                                tradeName
                                image
                            }
                        }
                        farmer{
                            id
                            name
                            surname
                            email
                            phone
                            image
                            address
                            farm{
                                id
                                name
                                sizeHectares
                                type
                                turnover
                                numberOfEmployees
                                province
                                latLong
                            }
                        }
                        orderStatus{
                            index
                            status
                            description
                        }
                        depot{
                            name
                        }
                    } 
                }`;
      graphqlRequestHandler(
        'farmInputInvoicesForSupplier',
        query,
        variables,
        headers,
      )
        .then((invoices: IFarmInputInvoice[]) => {
          invoices.forEach((invoice) => {
            invoice.currOrderStatus =
              invoice.orderStatus?.find(
                (item) => item.status === 'IN_PROGRESS',
              ) || invoice.orderStatus?.[0];
            if (invoice.farmer && !_IsLink(invoice.farmer?.image)) {
              invoice.farmer.image = '';
            }
          });
          resolve(invoices);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetDepotOrder(id: number): Promise<IFarmInputInvoice> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {id};
    return new Promise((resolve, reject) => {
      const query = `
                query variables($id: ID!){
                    farmInputInvoice(id: $id){
                        id
                        invoiceNumber
                        insertedDate
                        updatedDate
                        isPaid
                        latitude
                        longitude
                        address
                        total
                        outstanding
                        deliveryType
                        documentUrl
                        logisticsCost
                        vatExempt
                        state
                        proformaInvoiceUrl
                        orders{
                            id
                            quantity
                            price
                            isDelivered
                            dueDate
                            logisticsType
                            logisticsTypeName
                            product{
                                id
                                name
                                category
                                stockCode
                                size
                                price
                                measurement
                                grade
                                activeIngredient
                                tradeName
                                image
                            }
                        }
                        farmer{
                            id
                            name
                            surname
                            email
                            phone
                            image
                            address
                            farm{
                                id
                                name
                                sizeHectares
                                type
                                turnover
                                numberOfEmployees
                                province
                                latLong
                            }
                        }
                        orderStatus{
                            index
                            status
                            description
                        }
                        depot{
                            name
                        }
                    } 
                }`;
      graphqlRequestHandler('farmInputInvoice', query, variables, headers)
        .then((invoice: IFarmInputInvoice) => {
          invoice.currOrderStatus =
            invoice.orderStatus.find((item) => item.status === 'IN_PROGRESS') ||
            invoice.orderStatus[0];
          if (!_IsLink(invoice.farmer?.image)) {
            invoice.farmer.image = '';
          }
          resolve(invoice);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export type ReplaceProformaInvoiceLineItemInput = {
  id?: string;
  stockCode: string;
  price: number;
  quantity: number;
};

export type ReplaceProformaInvoiceProductsResult = {
  id: number;
  total: number;
  orders: Array<{
    id: string;
    product: {id: string; stockCode?: string};
    quantity: number;
    price: number;
  }>;
};

export async function apiReplaceProformaInvoiceProducts(
  proformaInvoiceId: string | number,
  products: ReplaceProformaInvoiceLineItemInput[],
): Promise<ReplaceProformaInvoiceProductsResult> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {proformaInvoiceId: String(proformaInvoiceId), products};
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation ReplaceProformaInvoiceProducts(
          $proformaInvoiceId: ID!,
          $products: [ReplaceProformaInvoiceLineItemInput!]!
        ) {
          replaceProformaInvoiceProducts(
            proformaInvoiceId: $proformaInvoiceId,
            products: $products
          ) {
            id
            total
            orders {
              id
              product {
                id
                stockCode
              }
              quantity
              price
            }
          }
        }
      `;
      console.log(variables);
      graphqlRequestHandler(
        'replaceProformaInvoiceProducts',
        mutation,
        variables,
        headers,
      )
        .then((result: ReplaceProformaInvoiceProductsResult) => resolve(result))
        .catch(reject);
    });
  });
}

export async function apiAddProformaInvoiceProduct(
  proformaInvoiceId: string | number,
  product: Omit<ReplaceProformaInvoiceLineItemInput, 'id'>,
): Promise<ReplaceProformaInvoiceProductsResult> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {
      proformaInvoiceId: String(proformaInvoiceId),
      product: {
        stockCode: product.stockCode,
        price: product.price,
        quantity: product.quantity,
      },
    };
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation AddProformaProduct(
          $proformaInvoiceId: ID!,
          $product: ReplaceProformaInvoiceLineItemInput!
        ) {
          addProformaInvoiceProduct(
            proformaInvoiceId: $proformaInvoiceId,
            product: $product
          ) {
            id
            total
            orders {
              id
              product {
                id
                stockCode
              }
              quantity
              price
            }
          }
        }
      `;
      graphqlRequestHandler(
        'addProformaInvoiceProduct',
        mutation,
        variables,
        headers,
      )
        .then((result: ReplaceProformaInvoiceProductsResult) => resolve(result))
        .catch(reject);
    });
  });
}

export async function apiRemoveProformaInvoiceProduct(
  proformaInvoiceId: string | number,
  stockCode: string,
): Promise<ReplaceProformaInvoiceProductsResult> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {
      proformaInvoiceId: String(proformaInvoiceId),
      stockCode,
    };
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation RemoveProformaProduct(
          $proformaInvoiceId: ID!,
          $stockCode: String!
        ) {
          removeProformaInvoiceProduct(
            proformaInvoiceId: $proformaInvoiceId,
            stockCode: $stockCode
          ) {
            id
            total
            orders {
              id
              product {
                id
                stockCode
              }
              quantity
              price
            }
          }
        }
      `;
      graphqlRequestHandler(
        'removeProformaInvoiceProduct',
        mutation,
        variables,
        headers,
      )
        .then((result: ReplaceProformaInvoiceProductsResult) => resolve(result))
        .catch(reject);
    });
  });
}

export type ConvertProformaToInvoiceResult = {
  id: number;
  total: number;
  documentUrl?: string;
};

export async function apiConvertProformaToInvoice(
  proformaInvoiceId: string | number,
): Promise<ConvertProformaToInvoiceResult> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {proformaInvoiceId: String(proformaInvoiceId)};
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation ConvertProformaToInvoice($proformaInvoiceId: ID!) {
          convertProformaToInvoice(proformaInvoiceId: $proformaInvoiceId) {
            id
            total
            documentUrl
          }
        }
      `;
      graphqlRequestHandler(
        'convertProformaToInvoice',
        mutation,
        variables,
        headers,
      )
        .then((result: ConvertProformaToInvoiceResult) => resolve(result))
        .catch(reject);
    });
  });
}

export async function apiGetDepotQuotes(
  limit?: number,
  farmerId?: number | string,
): Promise<IFarmInputQuote[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables: {
      limit?: number;
      farmerId?: number | string;
    } = {};

    if (typeof limit === 'number' && limit > 0) {
      variables.limit = limit;
    }
    if (farmerId !== undefined) {
      variables.farmerId = farmerId;
    }
    return new Promise((resolve, reject) => {
      const query = `
          query variables($limit: Int, $farmerId: ID){
              farmInputQuotationsForSupplier(limit: $limit, farmerId: $farmerId){
                  id
                  quoteNumber
                  insertedOn
                  updateDate
                  latitude
                  longitude
                  address
                  total
                  isForDelivery
                  state
                  isExpired
                  vatExempt
                  lineItems{
                      id
                      retailPrice
                      price
                      originalQuantity
                      quantity
                      product{
                          id
                          name
                          category
                          mainCategoryId
                          subCategoryId
                          subCategoryName
                          stockCode
                          size
                          price
                          measurement
                          grade
                          activeIngredient
                          tradeName
                          image
                      }
                  }
                  farmer{
                      id
                      userId
                      name
                      surname
                      email
                      phone
                      image
                      address
                      farm{
                          id
                          name
                          sizeHectares
                          type
                          turnover
                          numberOfEmployees
                          province
                          latLong
                      }
                  }
                  depot{
                      name
                  }
              }
          }`;
      graphqlRequestHandler(
        'farmInputQuotationsForSupplier',
        query,
        variables,
        headers,
      )
        .then((quotes: IFarmInputQuote[]) => {
          const mappedQuotes = quotes.map(mapQuoteResponse);
          resolve(mappedQuotes);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetDepotQuote(
  quoteId: number,
): Promise<IFarmInputQuote> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {quoteId};
    return new Promise((resolve, reject) => {
      const query = `
          query variable($quoteId: ID!){
              farmInputQuotationForSupplier(quotationId: $quoteId){
                  id
                  quoteNumber
                  insertedOn
                  updateDate
                  latitude
                  longitude
                  address
                  total
                  isForDelivery
                  state
                  isExpired
                  vatExempt
                  logisticsCost
                  contactPersonName
                  contactPersonPhoneNumber
                  deliveryInstructions
                  lineItems{
                      id
                      retailPrice
                      price
                      costPrice
                      originalQuantity
                      quantity
                      product{
                          id
                          name
                          category
                          mainCategoryId
                          subCategoryId
                          subCategoryName
                          stockCode
                          size
                          price
                          measurement
                          grade
                          activeIngredient
                          tradeName
                          image
                      }
                  }
                  farmer{
                      id
                      userId
                      name
                      surname
                      email
                      phone
                      image
                      address
                      farm{
                          id
                          name
                          sizeHectares
                          type
                          turnover
                          numberOfEmployees
                          province
                          latLong
                      }
                  }
                  depot{
                      id
                      name
                      address1
                      address2
                      address3
                      address4
                      address5
                      latitude
                      longitude
                  }
              } 
          }`;
      graphqlRequestHandler(
        'farmInputQuotationForSupplier',
        query,
        variables,
        headers,
      )
        .then((quote: IFarmInputQuote) => {
          resolve(mapQuoteResponse(quote));
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

const mapQuoteResponse = (quote: any): IFarmInputQuote => {
  if (!_IsLink(quote.farmer?.image)) {
    quote.farmer.image = '';
  }
  return {
    ...quote,
    id: parseInt(quote.id),
    formattedState: _ToTitleCase(quote.state.replaceAll('_', ' ')),
    deliveryType: quote.isForDelivery ? 'Delivery' : 'Collection',
  };
};

export async function apiUPDATEQUOTELINEITEM(
  lineItem: IFarmInputQuoteLineItem,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {
      productLineId: lineItem.id,
      quotedPrice: lineItem.price,
      quotedQuantity: lineItem.quantity,
    };
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $productLineId: ID!, 
            $quotedPrice: Float, 
            $quotedQuantity: Float
          ){
              updateFarmInputQuoteLineItem (
                productLineId: $productLineId, 
                quotedPrice: $quotedPrice, 
                quotedQuantity: $quotedQuantity
              ){
                  success
                  message
              } 
          }`;
      graphqlRequestHandler(
        'updateFarmInputQuoteLineItem',
        query,
        variables,
        headers,
      )
        .then((response: IGenericResponse) => {
          if (!response.success) throw new Error(response.message);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiDELETEQUOTELINEITEM(
  lineItemId: number,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {
      productLineId: lineItemId,
      delete: true,
    };
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $productLineId: ID!, 
            $delete: Boolean,
          ){
              updateFarmInputQuoteLineItem (
                productLineId: $productLineId, 
                delete: $delete, 
              ){
                  success
                  message
              } 
          }`;
      graphqlRequestHandler(
        'updateFarmInputQuoteLineItem',
        query,
        variables,
        headers,
      )
        .then((response: IGenericResponse) => {
          if (!response.success) throw new Error(response.message);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiADDQUOTELINEITEM(
  quoteId: number,
  lineItem: IFarmInputQuoteLineItem,
): Promise<IFarmInputQuote> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {
      quoteId,
      lineItem: {
        stockCode: lineItem.product.stockCode,
        price: lineItem.price,
        quantity: lineItem.quantity,
      },
    };
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $quoteId: ID!, 
            $lineItem: FarmInputQuoteLineItemInput!, 
          ){
              supplierAddFarmQuoteProductLine (
                quoteId: $quoteId, 
                lineItem: $lineItem, 
              ){
                  id
                  quoteNumber
              } 
          }`;
      graphqlRequestHandler(
        'supplierAddFarmQuoteProductLine',
        query,
        variables,
        headers,
      )
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiSUBMITQUOTE(
  quoteId: number,
): Promise<IFarmInputQuote> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {quoteId};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($quoteId: ID!){
            supplierConfirmFarmQuoteChanges (
              quoteId: $quoteId, 
            ){
              id
              quoteNumber
            } 
          }`;
      graphqlRequestHandler(
        'supplierConfirmFarmQuoteChanges',
        query,
        variables,
        headers,
      )
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

/**
 * Api to get messages for specific order
 * @param {number} invoiceId
 * @return {Promise}
 */
export async function apiGETORDERMESSAGES(
  invoiceId: number,
): Promise<IInputMessage[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const query = `
            query variables($invoiceId: ID!){
                getInputMessages(lastId: 0, invoiceIndex: $invoiceId){
                    id
                    invoiceIndex
                    mType
                    sent
                    messageRead
                    sender
                    textMessageData{
                        id
                        textMessage
                    }
                    replacementData{
                        id
                        orderId
                        previousQuantity
                        previousPrice
                        quantity
                        price
                        response
                        editable
                        previousProduct{
                            id
                            name
                            stockCode
                            category
                            image
                        }
                        replacementProduct{
                            id
                            name
                            stockCode
                            category
                            image
                        }
                    }
                } 
            }`;
      const variables = {invoiceId};
      graphqlRequestHandler('getInputMessages', query, variables, headers)
        .then((messages: any[]) => {
          const mappedMessages: IInputMessage[] = messages.map((message) => ({
            id: message.id,
            invoiceId: message.invoiceIndex,
            // todo message type comes back as string. should be number
            messageType:
              message.mType === '1'
                ? 'TEXT'
                : message.mType === '2'
                ? 'IMAGE'
                : 'PRODUCT',
            date: new Date(parseInt(message.sent)),
            isRead: message.messageRead,
            senderType: message.sender === 1 ? 'FARMER' : 'SUPPLIER',
            textData: !message.textMessageData
              ? undefined
              : {
                  id: message.textMessageData.id,
                  message: message.textMessageData.textMessage,
                },
            productData: !message.replacementData
              ? undefined
              : {
                  ...message.replacementData,
                  response:
                    message.replacementData.response === 0
                      ? 'PENDING'
                      : message.replacementData.response === 1
                      ? 'ACCEPTED'
                      : 'REIMBURSEMENT',
                  previousProduct: message.replacementData.previousProduct,
                  replacementProduct:
                    message.replacementData.replacementProduct,
                },
          }));
          resolve(mappedMessages);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

/**
 * Api to send order message
 * @param {number} invoiceId
 * @param {string} message
 * @return {Promise}
 */
export async function apiSENDTEXTORDERMESSAGE(
  invoiceId: number,
  message: string,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const query = `
            mutation variables($invoiceId: ID!, $message: String){
                inputChat(chatInput:{
                    invoiceIndex: $invoiceId
                    textMessage: $message,
                    mType:1
                }){
                    success
                    message
                }
            }`;
      const variables = {invoiceId, message};
      graphqlRequestHandler('inputChat', query, variables, headers)
        .then((response: IGenericResponse) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

/**
 * Api to send replacement order message
 * @param {IOrderReplacement} orderReplacement
 * @return {Promise}
 */
export async function apiSENDREPLACEMENTORDERMESSAGE(
  orderReplacement: IOrderReplacement,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {
      invoiceId: orderReplacement.invoiceId,
      orderId: orderReplacement.orderId,
      replacementProduct: orderReplacement.replacementProductStockCode,
      quantity: orderReplacement.quantity,
      price: orderReplacement.price,
    };
    return new Promise((resolve, reject) => {
      const query = `
                mutation variables(
                  $invoiceId: ID!, 
                  $orderId: ID!, 
                  $replacementProduct: String!, 
                  $quantity: Float!, 
                  $price: Float!){
                    inputChat(chatInput:{
                        mType:3,
                        invoiceIndex: $invoiceId
                        replacementInfo:{
                            orderId: $orderId
                            replacementProduct: $replacementProduct,
                            quantity: $quantity,
                            price: $price
                        }
                    }){
                        success
                        message
                    }
                }
            `;
      graphqlRequestHandler('inputChat', query, variables, headers)
        .then((response: IGenericResponse) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetInputProductsSummary(
  user: ISupplierUserProfile,
): Promise<IFarmInputProduct[]> {
  const queryName = user.isKhulaAdmin
    ? 'farmInputProducts'
    : 'farmInputProductsByMainSupplier';
  const queryVariables1 = user.isKhulaAdmin
    ? ''
    : 'variables($mainSupplierId: ID!)';
  const queryVariables2 = user.isKhulaAdmin
    ? ''
    : '(mainSupplierId: $mainSupplierId)';

  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const query = `
                query ${queryVariables1}{
                    ${queryName} ${queryVariables2}{
                        id
                        name
                        category
                        stockCode
                        price
                        image
                        isActive
                    } 
                }`;
      const variables = {mainSupplierId: user.mainSupplier?.id};
      graphqlRequestHandler(queryName, query, variables, headers)
        .then((products: IFarmInputProduct[]) => {
          resolve(products);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetInputProducts(
  user: ISupplierUserProfile,
  limit?: number,
  offset?: number,
): Promise<IFarmInputProduct[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const query = `
          query variables($limit: Int, $offset: Int){
              farmInputProductsForSupplier(limit: $limit, offset: $offset){
                  id
                  name
                  price
                  category
                  stockCode
                  image
                  size
                  measurement
                  productType
                  activeIngredient
                  tradeName
                  labelLink
                  mainCategoryId
                          subCategoryId
                  subCategoryName
                  isActive
                  grade
                  promotedPrice
                  mainSupplier{
                    id
                    name
                  }
                  brand{
                    id
                    name
                  }
              } 
          }`;
      const variables = {limit, offset};
      graphqlRequestHandler(
        'farmInputProductsForSupplier',
        query,
        variables,
        headers,
      )
        .then((products: IFarmInputProduct[]) => {
          resolve(products);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetInputProductNames(
  ids: number[],
): Promise<IFarmInputProduct[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const query = `
          query variables($ids: [ID!]){
              farmInputProducts(ids: $ids){
                  id
                  name
              } 
          }`;
      const variables = {ids};
      graphqlRequestHandler('farmInputProducts', query, variables, headers)
        .then((products: IFarmInputProduct[]) => resolve(products))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetInputProduct(
  id: string,
): Promise<IFarmInputProduct> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {id};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($id: ID!){
              farmInputProduct (id: $id){
                  id
                  name
                  costPrice
                  price
                  category
                  stockCode
                  image
                  size
                  measurement
                  productType
                  activeIngredient
                  tradeName
                  labelLink
                  mainCategoryId
                          subCategoryId
                  subCategoryName
                  isActive
                  grade
                  lengthCm
                  widthCm
                  heightCm
                  weightKg
                  promotedPrice
                  description
                  brand{
                    id
                    name
                    image
                  }
                  provincialRestrictions{
                    id
                    name
                  }
                  relatedProducts{
                    id
                    name
                  }
                  bugs{
                    id
                    name
                    image
                  }
              } 
          }`;
      graphqlRequestHandler('farmInputProduct', query, variables, headers)
        .then((product: IFarmInputProduct) => resolve(product))
        .catch((error) => reject(error));
    });
  });
}

export async function apiProductExistsWithStockCode(
  stockCode: string,
): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {stockCode};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($stockCode: String!){
              farmInputProductByStockCode(stockCode: $stockCode){
                  id
                  name
                  price
                  stockCode
              } 
          }`;
      graphqlRequestHandler(
        'farmInputProductByStockCode',
        query,
        variables,
        headers,
      )
        .then((product: IFarmInputProduct) => resolve(!!product))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateInputProduct(
  input: IFarmInputProductInput | Partial<IFarmInputProductInput>,
): Promise<IFarmInputProduct> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($input: FarmInputProductInput!){
              farmInputProductSave(input: $input){
                  id
                  name
                  costPrice
                  price
                  category
                  stockCode
                  image
                  size
                  measurement
                  productType
                  activeIngredient
                  tradeName
                  labelLink
                  mainCategoryId
                          subCategoryId
                  subCategoryName
                  isActive
                  grade
                  brand{
                    id
                    name
                    image
                  }
                  provincialRestrictions{
                    id
                    name
                  }
                  description
              } 
          }`;
      const variables = {input};
      graphqlRequestHandler('farmInputProductSave', query, variables, headers)
        .then((product: IFarmInputProduct) => {
          resolve(product);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiUpdateInputOrderPaymentStatus(
  invoiceId: number,
  isPaid: boolean,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const variables = {invoiceId, isPaid};
      const query = `
          mutation variables($invoiceId: ID!, $isPaid: Boolean!){
              farmInputInvoiceSaveOrderPaymentStatus(id: $invoiceId, isPaid: $isPaid){
                  id
                  isPaid
              }
          }`;
      graphqlRequestHandler(
        'farmInputInvoiceSaveOrderPaymentStatus',
        query,
        variables,
        headers,
      )
        .then(() => {
          resolve({success: true});
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

/**
 * Api to update order status
 * @param {number} invoiceId
 * @param {number} orderStatusId
 * @return {Promise}
 */
export async function apiFarmClearInvoiceDocument(
  invoiceId: number | string,
): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {invoiceId: String(invoiceId)};
    return new Promise((resolve, reject) => {
      const mutation = `
          mutation variables($invoiceId: ID!){
              farmClearInvoiceDocument(invoiceId: $invoiceId)
          }`;
      graphqlRequestHandler(
        'farmClearInvoiceDocument',
        mutation,
        variables,
        headers,
      )
        .then(() => resolve(true))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateInputOrderStatus(
  invoiceId: number,
  orderStatusId: number,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const variables = {invoiceId, orderStatusId};
      const query = `
                mutation variables($invoiceId: ID!, $orderStatusId: Int!){
                    farmInputInvoiceSaveOrderStatus(id: $invoiceId, orderStatusId: $orderStatusId){
                        id
                        orderStatus{
                            index
                            status
                            description
                        }
                    }
                }`;
      graphqlRequestHandler(
        'farmInputInvoiceSaveOrderStatus',
        query,
        variables,
        headers,
      )
        .then(() => {
          resolve({success: true});
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export function apiGetDepots(user: ISupplierUserProfile): Promise<IDepot[]> {
  return user.isKhulaAdmin
    ? apiGetAdminAllDepots()
    : user.mainSupplier
    ? apiGetSupplierActiveDepots(user.mainSupplier.id)
    : Promise.resolve([]);
}

export async function apiGetAdminAllDepots(ids?: string[]): Promise<IDepot[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const variables = {ids};
      const query = `
        query variables($ids: [ID!]){
          depots(ids: $ids){
            ${depotFields}
          }
        }
      `;
      graphqlRequestHandler('depots', query, variables, headers)
        .then((depots: IDepot[]) => resolve(mapDepotResponse(depots)))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetSupplierActiveDepots(
  mainSupplierId: string | number,
): Promise<IDepot[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const variables = {mainSupplierId};
      const query = `
        query variables($mainSupplierId: ID){
          supplierDepots(mainSupplierId: $mainSupplierId){
            ${depotFields}
          }
        }
      `;
      graphqlRequestHandler('supplierDepots', query, variables, headers)
        .then((depots: IDepot[]) => resolve(mapDepotResponse(depots)))
        .catch((error) => reject(error));
    });
  });
}

const depotFields = `
  id 
  name
  contact
  phone
  isActive
  latitude
  longitude
  address1
  address2
  address3
  address4
  address5
`;

const mapDepotResponse = (depots: IDepot[]) => {
  return depots
    .map((depot) => {
      depot.name = _ToTitleCase(depot.name);
      return depot;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

export async function apiDepotSave(input: IDepot): Promise<IDepot> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const variables = {input};
      const query = `
        mutation variables($input: DepotInput!){
          depotSave(input: $input){
            ${depotFields}
          }
        }
      `;
      graphqlRequestHandler('depotSave', query, variables, headers)
        .then((depot: IDepot) => resolve(depot))
        .catch((error) => reject(error));
    });
  });
}

export async function apiSENDQUOTETEXTMESSAGE(
  quoteId: number,
  message: string,
): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {quotationIndex: quoteId, textMessage: message, mType: 1};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variables($quotationIndex: ID!, $textMessage: String!, $mType: ID!){
            sendQuoteMessage(quotationIndex: $quotationIndex, textMessage: $textMessage, mType: $mType){
                id
            } 
        }`;
      graphqlRequestHandler('sendQuoteMessage', query, variables, headers)
        .then((_: IQuoteMessage) => {
          resolve(true);
        })
        .catch((error) => reject(error));
    });
  });
}

export async function apiGETQUOTEMESSAGES(
  quoteId: number,
): Promise<IQuoteMessage[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {quotationIndex: quoteId};
    return new Promise((resolve, reject) => {
      const query = `
        query variables($quotationIndex: ID!){
            getQuoteMessages(quotationIndex: $quotationIndex){
                id
                quotationIndex
                mType
                sent
                sender
                textMessage
            } 
        }`;
      graphqlRequestHandler('getQuoteMessages', query, variables, headers)
        .then((messages: any[]) => {
          const mappedMessages = messages.map((message) =>
            mapQuoteMessage(message),
          );
          resolve(mappedMessages);
        })
        .catch((error) => reject(error));
    });
  });
}

const mapQuoteMessage = (response: any): IQuoteMessage => {
  response.mType = parseInt(response.mType);
  const quoteMessage: IQuoteMessage = {
    id: response.id,
    quoteId: response.quotationId,
    messageType:
      response.mType === 1
        ? 'TEXT'
        : response.mType === 2
        ? 'IMAGE'
        : 'PRODUCT',
    message: response.textMessage,
    senderType: response.sender === 0 ? 'FARMER' : 'SUPPLIER',
    date: new Date(parseInt(response.sent)),
  };
  return quoteMessage;
};

export async function apiGetDepotStatistics(
  startDate?: Date,
  endDate?: Date,
  depotIds?: number[],
  farmerId?: string | number,
): Promise<IDepotStatistics> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {
      startDate: startDate?.toString(),
      endDate: endDate?.toString(),
      depotIds: depotIds,
      farmerId: farmerId,
    };
    return new Promise((resolve, reject) => {
      const query = `
          query variables($startDate: String, $endDate: String, $depotIds: [ID!], $farmerId: ID){
            depotStatistics(startDate: $startDate, endDate: $endDate, depotIds: $depotIds, farmerId: $farmerId){
              startDate
              endDate
              
              invoicesTotal
              invoicesCount
              paidInvoicesTotal
              paidInvoicesCount
              unpaidInvoicesTotal
              unpaidInvoicesCount
              completedInvoicesTotal
              completedInvoicesCount
              incompleteInvoicesTotal
              incompleteInvoicesCount
              paidAndIncompleteInvoicesTotal
              paidAndIncompleteInvoicesCount
              
              quotesTotal
              quotesCount
              activePendingQuotesTotal
              activePendingQuotesCount
              pendingQuotesTotal
              pendingQuotesCount
              completeQuotesTotal
              completeQuotesCount
              expiredQuotesTotal
              expiredQuotesCount
              rejectedQuotesTotal
              rejectedQuotesCount
            }
          }`;
      graphqlRequestHandler('depotStatistics', query, variables, headers)
        .then((stats: IDepotStatistics) => {
          stats.startDate = new Date(stats.startDate);
          stats.endDate = new Date(stats.endDate);
          resolve(stats);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetSupplierAnalytics(
  startDate: Date,
  endDate: Date,
  supplierId: number,
): Promise<ISupplierAnalytics> {
  const url = `${ANALYTICS_API}/getEvent/supplier/${supplierId}/${startDate.toISOString()}/${endDate.toISOString()}`;
  return fetch(url).then((response) => response.json());
}

export async function apiSearchFarmers(
  searchQuery: string,
  limit = 10,
): Promise<IFarmer[]> {
  return apiSearchFarmersByQuery('farmersBySearchQuery', searchQuery, limit);
}

export async function apiSearchFarmersAsSupplier(
  searchQuery: string,
  limit = 10,
): Promise<IFarmer[]> {
  return apiSearchFarmersByQuery('searchFarmersAsSupplier', searchQuery, limit);
}

function apiSearchFarmersByQuery(
  queryName: 'farmersBySearchQuery' | 'searchFarmersAsSupplier',
  searchQuery: string,
  limit = 10,
): Promise<IFarmer[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {
      searchQuery: searchQuery,
      limit: limit,
    };
    return new Promise((resolve, reject) => {
      const query = `
          query variables($searchQuery: String!, $limit: Int){
              ${queryName}(searchQuery: $searchQuery, limit: $limit){
                  id
                  name
                  surname
                  email
                  phone
                  image
                  address
                  userId
                  company {
                      vatExempt
                  }
                  farm{
                      id
                      name
                      sizeHectares
                      type
                      turnover
                      numberOfEmployees
                      fields{
                        info{
                            hectares
                            produce{
                              id
                              name
                            }
                          }
                      }
                      province
                      fields{
                            soilData{
                            soilType
                            P
                            K
                            Ca
                            Mg
                            Na
                            pH
                            cations
                            anions
                            exchangeableAcidity
                          }
                           id
                           coordinates{
                            lat
                            lng
                          }
                          risks{
                            analysisDate
                            ndviRiskLevel
                            moistureRiskLevel
                            clRiskLevel
                            averageRainFall
                            maxTemperature
                            minTemperature
                          }
                        info{
                          hectares
                          fieldName
                          variety
                          plantPopulation
                          irrigation
                          expectedYield
                          produce{
                            id
                            name
                          }
                        }
                  }
                      
                      
                        farmPolygons{
                   
                      
                      
                      
                      
                      
                      soilData{
                        soilType
                        P
                        K
                        Ca
                        Mg
                        Na
                        pH
                        cations
                        anions
                        exchangeableAcidity

                      }
                    }
                  }
                }
            }`;
      graphqlRequestHandler(queryName, query, variables, headers)
        .then((farmers: IFarmer[]) => {
          return resolve(farmers);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetNearestDepots(
  latitude: string,
  longitude: string,
): Promise<IDepot[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {latitude, longitude};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($latitude:String,$longitude:String){
              getDeports(latitude:$latitude,longitude:$longitude) {
                  id
                  deportId
                  name
                  address1
                  address2
                  address3
                  address4
                  address5
                  latitude
                  longitude
              }
          }`;
      graphqlRequestHandler('getDeports', query, variables, headers)
        .then((depots: IDepot[]) => {
          return resolve(depots);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiCreateQuote(
  farmerId: number,
  products: IInputQuoteProductInput[],
  deliveryAddress?: IInputQuoteAddress,
  isVatExempt?: boolean,
  specialInstructions?: {
    contactPersonName?: string;
    contactPersonPhoneNumber?: string;
    deliveryInstructions?: string;
  },
  logisticsCost?: number,
): Promise<ICreateQuoteResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {
      farmerId,
      inputs: products,
      deliveryAddress,
      vatExempted: isVatExempt,
      specialInstructions,
      logisticsCost,
    };
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $farmerId: ID, 
            $inputs: [FarmInputQuoteLineItemInput]!, 
            $deliveryAddress: DeliveryAddressInput,
            $vatExempted: Boolean,
            $specialInstructions: BuyInputsSpecialInstructions,
            $logisticsCost: Float,
          ){
              createFarmInputQuote(
                farmerId: $farmerId, 
                inputs: $inputs, 
                deliveryAddress: $deliveryAddress,
                vatExempted: $vatExempted,
                supplierIsCreating: true,
                specialInstructions: $specialInstructions
                logisticsCost: $logisticsCost
              ){
                  success
                  message
                  reference
                  quoteId
              }
            }`;
      graphqlRequestHandler('createFarmInputQuote', query, variables, headers)
        .then((response: ICreateQuoteResponse) => {
          if (response?.success) return resolve(response);
          else return reject(response.message);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiFarmClearQuoteDocument(
  quotationId: number | string,
): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {quotationId: String(quotationId)};
    return new Promise((resolve, reject) => {
      const mutation = `
          mutation variables($quotationId: ID!){
              farmClearQuoteDocument(quotationId: $quotationId)
          }`;
      graphqlRequestHandler(
        'farmClearQuoteDocument',
        mutation,
        variables,
        headers,
      )
        .then(() => resolve(true))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDownloadQuote(quoteId: number): Promise<string> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {quoteId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($quoteId: ID!){
            farmRequestQuotationDocument(quotationId: $quoteId, download: true){
              success
              message
            }
          }`;
      graphqlRequestHandler(
        'farmRequestQuotationDocument',
        query,
        variables,
        headers,
      )
        .then((response: IGenericResponse) => {
          if (response?.success) return resolve(response?.message ?? '');
          else return reject(response.message);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiRequestInvoiceDocument(
  invoiceId: number,
): Promise<string> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {invoiceId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($invoiceId: ID!){
            farmRequestInvoiceDocument(invoiceId: $invoiceId){
              success
              message
            }
          }`;
      graphqlRequestHandler(
        'farmRequestInvoiceDocument',
        query,
        variables,
        headers,
      )
        .then((response: IGenericResponse) => {
          if (response?.success) return resolve(response?.message ?? '');
          else return reject(response.message);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiDeleteQuote(quoteId: number): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {quoteId};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($quoteId: ID!){
            deleteQuote(quoteId: $quoteId){
              id
            }
          }`;
      graphqlRequestHandler('deleteQuote', query, variables, headers)
        .then(() => resolve(true))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetSupportCustomers(): Promise<ISupportCustomer[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query {
            supplierGetLinkedCustomers{
              farmerAccepted
              lastMessage{
                id
                from{
                  id
                  name
                  surname
                  image
                }
                to{
                  id
                  name
                  surname
                  image
                }
                type
                message
                insertedOn
              }
              farmer{
                id
                name
                surname
                email
                phone
                image
                address
                userId
                user{
                  verified
                }
                farm{
                    id
                    name
                    sizeHectares
                    type
                    turnover
                    numberOfEmployees
                    province
                    latLong
                }
              }
            }
          }`;
      graphqlRequestHandler(
        'supplierGetLinkedCustomers',
        query,
        variables,
        headers,
      )
        .then((customers: ISupportCustomer[]) => {
          customers.forEach((customer) => {
            if (!customer.lastMessage) return;
            customer.lastMessage.date = new Date(
              // @ts-ignore
              parseInt(customer.lastMessage.insertedOn),
            );
          });
          resolve(customers);
        })
        .catch((error) => reject(error));
    });
  });
}

export async function apiMoveSupportCustomer(
  farmerId: string,
  newSupplierId: string,
): Promise<void> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {farmerId, newSupplierId};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($farmerId: ID!, $newSupplierId: ID!){
            farmInputSupplierMoveCustomer(farmerId: $farmerId, newSupplierId: $newSupplierId){
              success
              message
            }
          }`;
      graphqlRequestHandler(
        'farmInputSupplierMoveCustomer',
        query,
        variables,
        headers,
      )
        .then((response: IGenericResponse) => {
          if (response.success) resolve();
          else reject(response.message);
        })
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetSupportForms(
  farmerId: string,
): Promise<ICompletedSupportForm[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {farmerId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($farmerId: ID!){
            getSupplierFarmerFormChat(farmerId: $farmerId){
              id
              from{
                id
              }
              to{
                id
              }
              type
              message
              insertedOn
            }
          }`;
      graphqlRequestHandler(
        'getSupplierFarmerFormChat',
        query,
        variables,
        headers,
      )
        .then((response: any) => {
          const forms: ICompletedSupportForm[] = response.map((form: any) => {
            const messages: ISupportMessage[] = form.map((message: any) =>
              mapSupportMessageResponse(message),
            );
            return messages;
          });
          resolve(forms);
        })
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetSupportMessages(
  myUserId: string,
  customerUserId: string,
  limit?: number,
  desc?: boolean,
): Promise<ISupportMessage[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {userId: customerUserId, limit, desc};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($userId: ID!, $limit: Int, $desc: Boolean){
            getSupplierMessages(userId: $userId, limit: $limit, desc: $desc){
              id
              from{
                id
              }
              to{
                id
              }
              type
              message
              insertedOn
            }
          }`;
      graphqlRequestHandler('getSupplierMessages', query, variables, headers)
        .then((response: any) => {
          const messages: ISupportMessage[] = response.map((message: any) =>
            mapSupportMessageResponse(message),
          );
          resolve(messages);
        })
        .catch((error) => reject(error));
    });
  });
}

const mapSupportMessageResponse = (
  response: any,
): ISupportMessage & {farmer: IFarmer} => {
  return {
    ...response,
    ...response?.lastMessage,
    date: new Date(
      parseInt(response?.lastMessage?.insertedOn ?? response?.insertedOn),
    ),
  };
};

export async function apiSendSupportMessage(
  customerUserId: string,
  message: string,
  type: 'TEXT' | 'IMAGE',
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {input: {id: customerUserId, type, message}};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($input: SupplierFarmerChatInput){
            sendSupplierMessage(input: $input){
              id
            }
          }`;
      graphqlRequestHandler('sendSupplierMessage', query, variables, headers)
        .then(() => resolve({success: true}))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetUser(userId: string): Promise<IUser> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {userId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($userId: ID){
            user(userId: $userId){
              id
              name
              surname
              email
              phone
              address
              latLong
              image
              farmer{
                id
                name
                surname
                email
                phone
                image
                address
                totalTraded
                farm{
                    id
                    name
                    sizeHectares
                    type
                    turnover
                    numberOfEmployees
                    province
                    latLong
                  fields{
                    id
                     coordinates{
                      lat
                      lng
                    }
                    availableLayers{
                      date
                      layers{
                        layer
                        url
                      }
                    }
  availableDroneLayers{
                      createdAt
                      analysisDate
                      rootLayerPathLink
                    }

                    risks{
                      analysisDate
                      ndviRiskLevel
                      moistureRiskLevel
                      clRiskLevel
                      averageRainFall
                      maxTemperature
                      minTemperature
                    }
                    info{
                      hectares
                      fieldName
                      variety
                      plantedOn
                      plantPopulation
                      irrigation
                      expectedYield
                      plantingDate
                      plannedPlantingDate
                      rowSpacingCm
                      seedSpacingCm
                      plantingDepthCm
                      harvestStartDate
                      harvestEndDate
                      produce{
                        id
                        name
                      }
                    }
                  }
                     farmPolygons{
                    coordinates{
                      lat
                      lng
                    }
                  }
                }
              }
            }
          }`;
      graphqlRequestHandler('user', query, variables, headers)
        .then((user: IUser) => resolve(user))
        .catch((error) => reject(error));
    });
  });
}

export async function apiSavePolygons(
  polygons: IPolygonInput,
  farmId: number,
): Promise<IGenericType> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {polygons, farmId};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($farmId:ID!,$polygons:[PolygonInput]) {
            saveFarmPolygonSupplier(
             farmId:$farmId,polygons:$polygons
            ) {
              success
              message
            }
          }`;
      graphqlRequestHandler(
        'saveFarmPolygonSupplier',
        query,
        variables,
        headers,
      )
        .then((response: IGenericType) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFarmField(
  fieldId: string | number,
  field: IPolygonInput,
): Promise<IGenericType> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {fieldId, field};
    return new Promise((resolve, reject) => {
      const query = `
          mutation UpdateFarmField($fieldId: ID!, $field: PolygonInput) {
            updateFarmField(fieldId: $fieldId, field: $field) {
              success
              message
            }
          }`;
      graphqlRequestHandler(`updateFarmField`, query, variables, headers)
        .then((response: IGenericType) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFarmField(
  fieldId: string | number,
): Promise<IGenericType> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {fieldId};
    return new Promise((resolve, reject) => {
      const query = `
          mutation DeleteFarmField($fieldId: ID!) {
            deleteFarmField(fieldId: $fieldId) {
              success
              message
            }
          }`;
      graphqlRequestHandler(`deleteFarmField`, query, variables, headers)
        .then((response: IGenericType) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUploadFile(
  file: File,
  fileKey: string,
  bucket: string = 'khula-farmer-profile-documents',
): Promise<string> {
  const form = new FormData();
  form.append('bucket', bucket);
  form.append('fileKey', fileKey);
  form.append('file', file);

  return fetch(FILE_API, {
    headers: {
      Authorization: await lshGETAUTH(),
      platform: 'WEB_SUPPLIER',
      requestSourceUserType: 'supplier',
    },
    method: 'POST',
    body: form,
  })
    .then((res) => res.json())
    .then((res) => res.url);
}

export type OnboardSupplierInput = {
  company: ISupplierCompanyProfile;
  companyPermissions: ISupplierCompanyPermissionsInput;
  depots: IDepot[];
  adminUser: ISupplierUserInput;
  /** Whether this company is a Commodity Group (true) or a standard Supplier (false) */
  isCommodityGroup?: boolean;
};

export async function apiOnboardSupplier(
  input: OnboardSupplierInput,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {input};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($input: OnboardInputSupplierInput!){
            onboardInputSupplier(input: $input){
              success
              message
            }
          }`;
      graphqlRequestHandler('onboardInputSupplier', query, variables, headers)
        .then((response: IGenericResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetWalletRestrictionList(): Promise<
  IWalletRestriction[]
> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query{
        getAccountRestrictionList{
          id
          name
        }
      }`;
      graphqlRequestHandler(
        'getAccountRestrictionList',
        query,
        variables,
        headers,
      )
        .then((categories: IWalletRestriction[]) => resolve(categories))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetInputProductSubCategories(): Promise<
  IFarmInputProductSubCategory[]
> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query{
            farmInputProductSubCategories{
              id
              code
              description
            }
          }`;
      graphqlRequestHandler(
        'farmInputProductSubCategories',
        query,
        variables,
        headers,
      )
        .then((categories: IFarmInputProductSubCategory[]) =>
          resolve(categories),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetInputProductSubSubCategories(): Promise<
  IFarmInputProductSubSubCategory[]
> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query{
            farmInputProductSubSubCategories{
              id
              name
              description
              subCategoryId
              code
            }
          }`;
      graphqlRequestHandler(
        'farmInputProductSubSubCategories',
        query,
        variables,
        headers,
      )
        .then((categories: IFarmInputProductSubSubCategory[]) =>
          resolve(categories),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetCompany(
  companyId: string,
): Promise<ISupplierCompanyProfile> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {companyId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($companyId: ID!){
            getSupplierCompany(companyId: $companyId){
              id
              name
              image
              location
              addressLine1
              addressLine2
              city
              province
              areaCode
              depotPriceList
              freeDeliveryOrderTotal
              marginPercentage
              bankId
              bankAccountTypeId
              bankAccountNumber
              bankBranchCode
            }
          }`;
      graphqlRequestHandler('getSupplierCompany', query, variables, headers)
        .then((company: ISupplierCompanyProfile) => resolve(company))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateCompany(
  company: ICompanyInput,
): Promise<ISupplierCompanyProfile> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {
      input: company,
    };
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($input: SupplierCompanyInput!){
            supplierCompanySave(input: $input){
              id
              name
              image
              location
              addressLine1
              addressLine2
              city
              province
              areaCode
              depotPriceList
              freeDeliveryOrderTotal
            }
          }`;
      graphqlRequestHandler('companySave', query, variables, headers)
        .then((company: ISupplierCompanyProfile) => resolve(company))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdatePassword(
  oldPassword: string,
  newPassword: string,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const variables = {oldPassword, newPassword};
      const query = `
          mutation variables($oldPassword: String!, $newPassword: String!){
              farmInputSupplierUpdatePassword(oldPassword: $oldPassword, newPassword: $newPassword){
                  success
                  message
              }
          }`;
      graphqlRequestHandler(
        'farmInputSupplierUpdatePassword',
        query,
        variables,
        headers,
      )
        .then((response: IGenericResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetProductDepotInfo(args: {
  ids?: string[];
  productIds?: string[];
  depotIds?: string[];
}): Promise<IFarmInputProductDepotInfo[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const variables = args;
      const query = `
          query variables($productIds: [ID!], $depotIds: [ID!]){
              farmInputProductDepotInfo(productIds: $productIds, depotIds: $depotIds){
                  id
                  isActive
                  depotId
                  depot{
                    id
                    name
                  }
                  productId
                  stockOnHand 
                  minOrderQuantityFreeDelivery
                  minOrderQuantity
                  price
                  costPrice
              }
          }`;
      graphqlRequestHandler(
        'farmInputProductDepotInfo',
        query,
        variables,
        headers,
      )
        .then((response: IFarmInputProductDepotInfo[]) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiSaveProductDepotInfo(
  inputs: IFarmInputProductDepotInfoInput[],
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    return new Promise((resolve, reject) => {
      const variables = {inputs};
      const query = `
          mutation variables($inputs: [FarmInputProductDepotInfoInput!]!){
              farmInputProductDepotInfoBulkSave(inputs: $inputs)
          }`;
      graphqlRequestHandler(
        'farmInputProductDepotInfoBulkSave',
        query,
        variables,
        headers,
      )
        .then(() => resolve({success: true}))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetProvinces(): Promise<IProvince[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query{
            provinces{
              id
              name
            }
          }`;
      graphqlRequestHandler('provinces', query, variables, headers)
        .then((provinces: IProvince[]) => resolve(provinces))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetBugs(): Promise<IBug[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query{
            getAllInputBugs{
              id
              name
            }
          }`;
      graphqlRequestHandler('getAllInputBugs', query, variables, headers)
        .then((provinces: IBug[]) => resolve(provinces))
        .catch((error) => reject(error));
    });
  });
}

export async function apiCreateProductPromotion(
  promotion: IProductPromoteInput,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = promotion;
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $promotionalValue: Float!
            $promotionalStartDate: String!
            $promotionalEndDate: String!
            $inputProductId: ID!
            $depots: [ID!]
          ){
            createProductPromotion(
              promotionalValue: $promotionalValue
              promotionalStartDate: $promotionalStartDate
              promotionalEndDate: $promotionalEndDate
              inputProductId: $inputProductId
              depots: $depots
            ){
              success
              message
            }
          }`;
      graphqlRequestHandler('createProductPromotion', query, variables, headers)
        .then((response: IGenericResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiCancelProductPromotion(
  productIds: number[],
  depotsIds: number[],
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {
      inputProductId: productIds,
      depots: depotsIds,
    };
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $inputProductId: [ID!]
            $depots: [ID!]
          ){
            deactivatePromotion(
              inputProductId: $inputProductId
              depots: $depots
            ){
              success
              message
            }
          }`;
      graphqlRequestHandler('deactivatePromotion', query, variables, headers)
        .then((response: IGenericResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetSupplierPermissions(
  userId?: number | string,
): Promise<ISupplierUserPermission[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {userId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables(
            $userId: ID
            $limit: Int
            $offset: Int
          ){
            farmInputSupplierPermissions(
              userId: $userId
              limit: $limit
              offset: $offset
            ){
              id
              name
              description
            }
          }`;
      graphqlRequestHandler(
        'farmInputSupplierPermissions',
        query,
        variables,
        headers,
      )
        .then((response: ISupplierUserPermission[]) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateSupplierPermissions(
  userId: number | string,
  permissionIds: (number | string)[],
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {userId, permissionIds};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $userId: ID!
            $permissionIds: [ID!]!
          ){
            FarmInputSupplierPermissionsSave(
              userId: $userId
              permissionIds: $permissionIds
            )
          }`;
      graphqlRequestHandler(
        'farmInputSupplierPermissions',
        query,
        variables,
        headers,
      )
        .then(() => resolve({success: true}))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetQuoteSupportingDocuments(
  quoteId: number | string,
): Promise<ISupportingDocument[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {quoteId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables(
            $quoteId: ID!
          ){
            farmInputQuoteSupportingDocuments(
              quoteId: $quoteId
            ){
              id
              quoteId
              title
              url
              createdDate
            }
          }`;
      graphqlRequestHandler(
        'farmInputQuoteSupportingDocuments',
        query,
        variables,
        headers,
      )
        .then((response: ISupportingDocument[]) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiSaveQuoteSupportingDocument(
  input: IQuoteSupportingDocumentInput,
): Promise<ISupportingDocument> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {input};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $input: QuoteSupportingDocumentInput!
          ){
            farmInputQuoteSupportingDocumentSave(
              input: $input
            ){
              id
              quoteId
              title
              url
              createdDate
            }
          }`;
      graphqlRequestHandler(
        'farmInputQuoteSupportingDocumentSave',
        query,
        variables,
        headers,
      )
        .then((response: ISupportingDocument) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteQuoteSupportingDocument(
  documentId: number | string,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: documentId};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $id: ID!
          ){
            farmInputQuoteSupportingDocumentDelete(id: $id)
          }`;
      graphqlRequestHandler(
        'farmInputQuoteSupportingDocumentDelete',
        query,
        variables,
        headers,
      )
        .then(() => resolve({success: true}))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetInvoiceSupportingDocuments(
  invoiceId: number | string,
): Promise<ISupportingDocument[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {invoiceId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables(
            $invoiceId: ID!
          ){
            farmInputInvoiceSupportingDocuments(
              invoiceId: $invoiceId
            ){
              id
              invoiceId
              title
              url
              createdDate
            }
          }`;
      graphqlRequestHandler(
        'farmInputInvoiceSupportingDocuments',
        query,
        variables,
        headers,
      )
        .then((response: ISupportingDocument[]) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiSaveInvoiceSupportingDocument(
  input: IInvoiceSupportingDocumentInput,
): Promise<ISupportingDocument> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {input};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $input: InvoiceSupportingDocumentInput!
          ){
            farmInputInvoiceSupportingDocumentSave(
              input: $input
            ){
              id
              invoiceId
              title
              url
              createdDate
            }
          }`;
      graphqlRequestHandler(
        'farmInputInvoiceSupportingDocumentSave',
        query,
        variables,
        headers,
      )
        .then((response: ISupportingDocument) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteInvoiceSupportingDocument(
  documentId: number | string,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: documentId};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $id: ID!
          ){
            farmInputInvoiceSupportingDocumentDelete(id: $id)
          }`;
      graphqlRequestHandler(
        'farmInputInvoiceSupportingDocumentDelete',
        query,
        variables,
        headers,
      )
        .then(() => resolve({success: true}))
        .catch((error) => reject(error));
    });
  });
}
export async function apiCalculateDeliveryCost(
  startLocation: IDeliveryLocationInput,
  endLocation: IDeliveryLocationInput,
  products: IDeliveryProductInput[],
): Promise<IDeliveryCost> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {startLocation, endLocation, products};
    return new Promise((resolve, reject) => {
      const query = `
          query variables(
            $startLocation: DeliveryLocationInput!, 
            $endLocation: DeliveryLocationInput!, 
            $products: [DeliveryProductInput!]!
          ){
            calculateDeliveryCost(
              startLocation: $startLocation, 
              endLocation: $endLocation, 
              products: $products,
            ){
              success
              message
              totalDeliveryCost
              originalTotalDeliveryCost
            }
          }`;
      graphqlRequestHandler('calculateDeliveryCost', query, variables, headers)
        .then((response: IDeliveryCost) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiFarmerCreate(
  input: IFarmerSignUpInput,
): Promise<IGenericResponse> {
  return lshGETAUTH().then(() => {
    const headers = {};
    const variables = {input};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($input: signUpInput!){
                signUp(input: $input) {
                    success
                    message
                }
            }`;
      graphqlRequestHandler('signUp', query, variables, headers)
        .then((response: IGenericResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiFarmerInvite(
  emails: string[],
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {emails};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($emails: [String!]!){
              supplierFarmerInvitation(emails: $emails) {
                message
                success
              }
          }`;
      graphqlRequestHandler(
        'supplierFarmerInvitation',
        query,
        variables,
        headers,
      )
        .then((response: IGenericResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFarmer(email: string): Promise<IFarmer> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {email};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($email: String){
              farmerByEmail(email: $email) {
                  id
                  name
                  surname
                  email
                  phone
                  image
                  address
                  userId
                  farm{
                  
                  
                  fields{
                            soilData{
                            soilType
                            P
                            K
                            Ca
                            Mg
                            Na
                            pH
                            cations
                            anions
                            exchangeableAcidity
                          }
                           id
                           coordinates{
                            lat
                            lng
                          }
                          risks{
                            analysisDate
                            ndviRiskLevel
                            moistureRiskLevel
                            clRiskLevel
                            averageRainFall
                            maxTemperature
                            minTemperature
                          }
                        info{
                          hectares
                          fieldName
                          variety
                          plantPopulation
                          irrigation
                          expectedYield
                          produce{
                            id
                            name
                          }
                        }
                  }
                  
                      id
                      name
                      sizeHectares
                      type
                      turnover
                      numberOfEmployees
                      province
                        farmPolygons{
                    
                      soilData{
                        soilType
                        P
                        K
                        Ca
                        Mg
                        Na
                        pH
                        cations
                        anions
                        exchangeableAcidity

                      }
                    }
                  }
              }
            }`;
      graphqlRequestHandler('farmerByEmail', query, variables, headers)
        .then((response: IFarmer) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiFarmerExists(email: string): Promise<IFarmer> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {email};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($email: String){
              farmerByEmail(email: $email) {
                  id
                  name
                  surname
                  email
                  phone
                  image
                  address
                  userId
                  farm{
                    id
                    name
                  }
              }
            }`;
      graphqlRequestHandler('farmerByEmail', query, variables, headers)
        .then((response: IFarmer) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFarmerAddress(
  farmerId: number | string,
  addressInput: IFarmerAddressInput,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {farmerId, addressInput};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($farmerId: ID, $addressInput: FarmerAddressInput!){
              farmerUpdateAddress(farmerId: $farmerId, addressInput: $addressInput) {
                  success
                  message
              }
            }`;
      graphqlRequestHandler('farmerUpdateAddress', query, variables, headers)
        .then((response: IGenericResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetPurchaseOrdersByInvoice(
  invoiceId: number,
): Promise<IFarmInputPurchaseOrder[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {invoiceId};
    return new Promise((resolve, reject) => {
      const query = `
        query variables($invoiceId: ID) {
          farmInputPurchaseOrders(invoiceId: $invoiceId) {
            id
            createdDate
            updatedDate
            documentUrl
            gpayReceiptNo
            gpayWithdrawDate
            trackingReference
            expectedPaymentDate
            mainSupplier { name }
            total
            orders{
                quantity
                price
            }
            supportingDocuments {
              id
              title
              url
              createdDate
            }
            gpayWithdrawUser {
              id
              name
              surname
            }
            statusUpdates {
              id
              date
              status { id name }
              user { name surname }
            }
            invoice {
              id
              invoiceNumber
              insertedDate
              updatedDate
              isPaid
              total
              outstanding
              deliveryType
              logisticsCost
              vatExempt
              orders {
                id
                quantity
                price
                isDelivered
                dueDate
                logisticsType
                logisticsTypeName
                product {
                  id
                  name
                  category
                  stockCode
                  size
                  price
                  measurement
                  grade
                  activeIngredient
                  tradeName
                  image
                }
              }
              farmer {
                id
                name
                surname
                email
                phone
                image
                address
                farm { id name sizeHectares province }
              }
              depot {
                name
                mainSupplier { name }
              }
            }
          }
        }`;
      graphqlRequestHandler(
        'farmInputPurchaseOrders',
        query,
        variables,
        headers,
      )
        .then((purchaseOrders: IFarmInputPurchaseOrder[]) =>
          resolve(purchaseOrders),
        )
        .catch(reject);
    });
  });
}

export async function apiGetPurchaseOrders(
  limit?: number,
): Promise<IFarmInputPurchaseOrder[]> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {limit};
    return new Promise((resolve, reject) => {
      const query = `
                query variables($limit: Int){
                    farmInputPurchaseOrders(limit: $limit){
                        id
                        createdDate
                        updatedDate
                        documentUrl
                        gpayReceiptNo
                        gpayWithdrawDate
                        trackingReference
                        expectedPaymentDate
                        gpayWithdrawUser{
                          id
                          name
                          surname
                        }
                        statusUpdates{
                            id
                            date
                            status{
                                id
                                name
                            }
                            user{
                                name
                                surname
                            }
                        }
                        orders{
                            id
                            quantity
                            price
                            isDelivered
                            dueDate
                            logisticsType
                            logisticsTypeName
                            product{
                                id
                                name
                                category
                                stockCode
                                size
                                price
                                measurement
                                grade
                                activeIngredient
                                tradeName
                                image
                            }
                        }
                        invoice{
                            id
                            invoiceNumber
                            insertedDate
                            updatedDate
                            isPaid
                            latitude
                            longitude
                            address
                            total
                            outstanding
                            deliveryType
                            logisticsCost
                            vatExempt
                            farmer{
                                id
                                name
                                surname
                                email
                                phone
                                image
                                address
                                farm{
                                    id
                                    name
                                    sizeHectares
                                    type
                                    turnover
                                    numberOfEmployees
                                    province
                                    latLong
                                }
                            }
                            depot{
                                name
                                mainSupplier{
                                    name
                                }
                            }
                        }
                    } 
                }`;
      graphqlRequestHandler(
        'farmInputPurchaseOrders',
        query,
        variables,
        headers,
      )
        .then((purchaseOrders: IFarmInputPurchaseOrder[]) => {
          resolve(purchaseOrders);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetPurchaseOrder(input: {
  id?: number;
  invoiceId?: number;
}): Promise<IFarmInputPurchaseOrder> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = input;
    return new Promise((resolve, reject) => {
      const query = `
                query variables($id: ID, $invoiceId: ID){
                    farmInputPurchaseOrder(id: $id, invoiceId: $invoiceId){
                        id
                        createdDate
                        updatedDate
                        documentUrl
                        gpayReceiptNo
                        gpayWithdrawDate
                        trackingReference
                        expectedPaymentDate
                        mainSupplier{
                            id
                            name
                        }
                        orders{
                            id
                            quantity
                            price
                            isDelivered
                            dueDate
                            logisticsType
                            logisticsTypeName
                            product{
                                id
                                name
                                category
                                stockCode
                                size
                                price
                                measurement
                                grade
                                activeIngredient
                                tradeName
                                image
                                mainSupplier{
                                    id
                                    name
                                }
                                depot{
                                    id
                                    name
                                    mainSupplier{
                                        name
                                    }
                                }
                            }
                        }
                        gpayWithdrawUser{
                          id
                          name
                          surname
                        }
                        statusUpdates{
                            id
                            date
                            status{
                                id
                                name
                            }
                            user{
                                name
                                surname
                            }
                        }
                        invoice{
                            id
                            invoiceNumber
                            insertedDate
                            updatedDate
                            isPaid
                            latitude
                            longitude
                            address
                            total
                            outstanding
                            deliveryType
                            logisticsCost
                            vatExempt
                            farmer{
                                id
                                name
                                surname
                                email
                                phone
                                image
                                address
                                farm{
                                    id
                                    name
                                    sizeHectares
                                    type
                                    turnover
                                    numberOfEmployees
                                    province
                                    latLong
                                }
                            }
                            depot{
                            id
                                name
                                mainSupplier{
                                    name
                                }
                            }
                        }
                    } 
                }`;
      graphqlRequestHandler('farmInputPurchaseOrder', query, variables, headers)
        .then((purchaseOrder: IFarmInputPurchaseOrder) => {
          resolve(purchaseOrder);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiUpdatePurchaseOrderStatus(
  id: number,
  statusId: number,
): Promise<IFarmInputPurchaseOrder> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {id, statusId};
    return new Promise((resolve, reject) => {
      const query = `
                mutation variables($id: ID!, $statusId: ID!){
                    farmInputPurchaseOrderUpdateStatus(id: $id, statusId: $statusId){
                        id
                        createdDate
                        updatedDate
                        documentUrl
                        gpayReceiptNo
                        gpayWithdrawDate
                        trackingReference
                        expectedPaymentDate
                        gpayWithdrawUser{
                          id
                          name
                          surname
                        }
                        statusUpdates{
                            id
                            date
                            status{
                                id
                                name
                            }
                            user{
                                name
                                surname
                            }
                        }
                        invoice{
                            id
                            invoiceNumber
                            insertedDate
                            updatedDate
                            isPaid
                            latitude
                            longitude
                            address
                            total
                            outstanding
                            deliveryType
                            logisticsCost
                            vatExempt
                            orders{
                                id
                                quantity
                                price
                                isDelivered
                                dueDate
                                logisticsType
                                logisticsTypeName
                                product{
                                    id
                                    name
                                    category
                                    stockCode
                                    size
                                    price
                                    measurement
                                    grade
                                    activeIngredient
                                    tradeName
                                    image
                                }
                            }
                            farmer{
                                id
                                name
                                surname
                                email
                                phone
                                image
                                address
                                farm{
                                    id
                                    name
                                    sizeHectares
                                    type
                                    turnover
                                    numberOfEmployees
                                    province
                                    latLong
                                }
                            }
                            depot{
                                name
                                mainSupplier{
                                    name
                                }
                            }
                        }
                    } 
                }`;
      graphqlRequestHandler(
        'farmInputPurchaseOrderUpdateStatus',
        query,
        variables,
        headers,
      )
        .then((purchaseOrder: IFarmInputPurchaseOrder) => {
          resolve(purchaseOrder);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiSavePurchaseOrder(input: {
  id?: number;
  invoiceId?: number;
  trackingReference?: string;
  expectedPaymentDate?: Date;
}): Promise<IFarmInputPurchaseOrder> {
  return lshGETAUTH().then((data) => {
    const headers = {
      Authorization: data,
    };
    const variables = {input};
    return new Promise((resolve, reject) => {
      const query = `
                mutation variables($input: FarmInputPurchaseOrderInput!){
                    farmInputPurchaseOrderSave(input: $input){
                        id
                        createdDate
                        updatedDate
                        documentUrl
                        gpayReceiptNo
                        gpayWithdrawDate
                        trackingReference
                        expectedPaymentDate
                        gpayWithdrawUser{
                          id
                          name
                          surname
                        }
                        statusUpdates{
                            id
                            date
                            status{
                                id
                                name
                            }
                            user{
                                name
                                surname
                            }
                        }
                        invoice{
                            id
                            invoiceNumber
                            insertedDate
                            updatedDate
                            isPaid
                            latitude
                            longitude
                            address
                            total
                            outstanding
                            deliveryType
                            vatExempt
                            logisticsCost
                            orders{
                                id
                                quantity
                                price
                                isDelivered
                                dueDate
                                logisticsType
                                logisticsTypeName
                                product{
                                    id
                                    name
                                    category
                                    stockCode
                                    size
                                    price
                                    measurement
                                    grade
                                    activeIngredient
                                    tradeName
                                    image
                                }
                            }
                            farmer{
                                id
                                name
                                surname
                                email
                                phone
                                image
                                address
                                farm{
                                    id
                                    name
                                    sizeHectares
                                    type
                                    turnover
                                    numberOfEmployees
                                    province
                                    latLong
                                }
                            }
                            depot{
                                name
                                mainSupplier{
                                    name
                                }
                            }
                        }
                    } 
                }`;
      graphqlRequestHandler(
        'farmInputPurchaseOrderSave',
        query,
        variables,
        headers,
      )
        .then((purchaseOrder: IFarmInputPurchaseOrder) => {
          resolve(purchaseOrder);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetPurchaseOrderStatuses(): Promise<IGenericType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query{
            farmInputPurchaseOrderStatuses{
              id
              name
            }
          }`;
      graphqlRequestHandler(
        'farmInputPurchaseOrderStatuses',
        query,
        variables,
        headers,
      )
        .then((purchaseOrderStatuses: IGenericType[]) =>
          resolve(purchaseOrderStatuses),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetBankTypes(): Promise<IGenericType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query{
              getBankTypes{
                  id
                  name
              }
            }`;
      graphqlRequestHandler('getBankTypes', query, variables, headers)
        .then((response: IGenericType[]) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetBankAccountTypes(): Promise<IGenericType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query{
              getBankAccountTypes{
                  id
                  name
              }
            }`;
      graphqlRequestHandler('getBankAccountTypes', query, variables, headers)
        .then((response: IGenericType[]) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetWalletStatement(
  user: ISupplierUserProfile,
  startDate: Date,
  endDate: Date,
): Promise<IWalletStatement> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {
      companyId: user.company?.id || '',
      period: {startDate, endDate},
    };
    return new Promise((resolve, reject) => {
      const query = `
          query variables($companyId: ID, $period: StatementPeriodInput){
              getClientWalletAccountStatement(companyId: $companyId, period: $period) {
                  accountNumber
                  fromDate
                  toDate
                  AccountBalance
                  OutstandingRequisitions
                  AvailableBalance
                  Statement {
                      TransactionDate
                      SyncDate
                      Description
                      Reference
                      SupplierBankReference
                      Amount
                      inputsPurchaseOrder {
                          id
                          gpayReceiptNo
                          gpayWithdrawDate
                          gpayWithdrawUser {
                              id
                              name
                              surname
                          }
                          expectedPaymentDate
                      }
                  }
              }
          }`;
      graphqlRequestHandler(
        'getClientWalletAccountStatement',
        query,
        variables,
        headers,
      )
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiCreateWallet(): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          mutation{
              createSupplierGpayWallet{
                  success
                  message
              }
          }`;
      graphqlRequestHandler(
        'createSupplierGpayWallet',
        query,
        variables,
        headers,
      )
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiWalletWithdraw(
  pin: string,
  purchaseOrderId: number,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {pin, purchaseOrderId};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($pin: String!, $purchaseOrderId: ID!){
              supplierWithdraw(pin: $pin, purchaseOrderId: $purchaseOrderId){
                  success
                  message
              }
          }`;
      graphqlRequestHandler('supplierWithdraw', query, variables, headers)
        .then((response) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUserHasWalletPinCode(
  userId: string,
): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {userId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($userId: ID){
            user(userId: $userId){
              id
              hasPin
            }
          }`;
      graphqlRequestHandler('user', query, variables, headers)
        .then((user: IUser) => resolve(user.hasPin))
        .catch((error) => reject(error));
    });
  });
}

export async function apiConfirmUserPassword(
  password: string,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {password};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($password: String!){
            assertUserPassword(password: $password){
              success
              message
            }
          }`;
      graphqlRequestHandler('assertUserPassword', query, variables, headers)
        .then((response: IGenericResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateWalletPin(
  pinCode: string,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {input: {pinCode}};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($input: userInput){
            updateUserAttribute(input: $input){
              success
              message
            }
          }`;
      graphqlRequestHandler('updateUserAttribute', query, variables, headers)
        .then((response: IGenericResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}
export async function apiGetPurchaseOrderSupportingDocuments(
  purchaseOrderId: number | string,
): Promise<ISupportingDocument[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {purchaseOrderId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables(
            $purchaseOrderId: ID!
          ){
            farmInputPurchaseOrderSupportingDocuments(
              purchaseOrderId: $purchaseOrderId
            ){
              id
              purchaseOrderId
              title
              url
              createdDate
            }
          }`;
      graphqlRequestHandler(
        'farmInputPurchaseOrderSupportingDocuments',
        query,
        variables,
        headers,
      )
        .then((response: ISupportingDocument[]) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiSavePurchaseOrderSupportingDocument(
  input: IPurchaseOrderSupportingDocumentInput,
): Promise<ISupportingDocument> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {input};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $input: PurchaseOrderSupportingDocumentInput!
          ){
            farmInputPurchaseOrderSupportingDocumentSave(
              input: $input
            ){
              id
              purchaseOrderId
              title
              url
              createdDate
            }
          }`;
      graphqlRequestHandler(
        'farmInputPurchaseOrderSupportingDocumentSave',
        query,
        variables,
        headers,
      )
        .then((response: ISupportingDocument) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeletePurchaseOrderSupportingDocument(
  documentId: number | string,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: documentId};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables(
            $id: ID!
          ){
            farmInputPurchaseOrderSupportingDocumentDelete(id: $id)
          }`;
      graphqlRequestHandler(
        'farmInputPurchaseOrderSupportingDocumentDelete',
        query,
        variables,
        headers,
      )
        .then(() => resolve({success: true}))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetInvoiceBundles(
  limit?: number,
): Promise<IInvoiceBundle[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {limit};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($limit: Int){
              farmInputBundledInvoices(limit: $limit){
                  id
                  insertedOn
                  updatedOn
                  total
                  outstanding
                  allInvoicesPaid
                  invoices{
                      id
                      invoiceNumber
                      insertedDate
                      updatedDate
                      isPaid
                      latitude
                      longitude
                      address
                      total
                      outstanding
                      deliveryType
                      vatExempt
                      depot{
                          name
                          mainSupplier{
                              name
                          }
                      }
                      farmer{
                          id
                          name
                          surname
                          email
                          phone
                          image
                          address
                          farm{
                              id
                              name
                              sizeHectares
                              type
                              turnover
                              numberOfEmployees
                              province
                              latLong
                          }
                      }
                  }
              } 
          }`;
      graphqlRequestHandler(
        'farmInputBundledInvoices',
        query,
        variables,
        headers,
      )
        .then((invoiceBundles: IInvoiceBundle[]) => {
          resolve(invoiceBundles);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetInvoiceBundle(id: number): Promise<IInvoiceBundle> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($id: ID){
              farmInputBundledInvoice(id: $id){
                  id
                  insertedOn
                  updatedOn
                  total
                  outstanding
                  allInvoicesPaid
                  invoices{
                      id
                      invoiceNumber
                      insertedDate
                      updatedDate
                      isPaid
                      latitude
                      longitude
                      address
                      total
                      outstanding
                      deliveryType
                      vatExempt
                      depot{
                          name
                          mainSupplier{
                              name
                          }
                      }
                      farmer{
                          id
                          name
                          surname
                          email
                          phone
                          image
                          address
                          farm{
                              id
                              name
                              sizeHectares
                              type
                              turnover
                              numberOfEmployees
                              province
                              latLong
                          }
                      }
                  }
              } 
          }`;
      graphqlRequestHandler(
        'farmInputBundledInvoice',
        query,
        variables,
        headers,
      )
        .then((invoiceBundle: IInvoiceBundle) => {
          resolve(invoiceBundle);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
}

export async function apiGetCreditNoteByInvoice(
  invoiceId: number | string,
): Promise<IFarmInputCreditNote | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {invoiceId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($invoiceId: ID!){
              farmInputCreditNoteByInvoice(invoiceId: $invoiceId){
                  id
                  insertedDate
                  updatedDate
                  invoiceId
                  documentUrl
                  lineItems{
                    id
                    creditNoteId
                    orderId
                    quantity
                    price
                    order{
                      id
                      price
                      quantity
                      product{
                        name
                      }
                    }
                  }
              } 
          }`;
      graphqlRequestHandler(
        'farmInputCreditNoteByInvoice',
        query,
        variables,
        headers,
      )
        .then((creditNote: IFarmInputCreditNote) => resolve(creditNote))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateCreditNote(
  invoiceId: number | string,
  lineItems: IFarmInputCreditNoteLineItemInput[],
): Promise<IFarmInputCreditNote> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {input: {invoiceId, lineItems}};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($input: FarmInputCreditNoteInput!){
              farmInputCreditNoteSave(input: $input){
                  id
                  insertedDate
                  updatedDate
                  invoiceId
                  lineItems{
                    id
                    creditNoteId
                    orderId
                    quantity
                    price
                    order{
                      id
                      price
                      quantity
                      product{
                        name
                      }
                    }
                  }
              } 
          }`;
      graphqlRequestHandler(
        'farmInputCreditNoteSave',
        query,
        variables,
        headers,
      )
        .then((creditNote: IFarmInputCreditNote) => resolve(creditNote))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteCreditNoteLineItem(
  id: number | string,
): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($id: ID!){
              farmInputCreditNoteLineItemDelete(id: $id)
          }`;
      graphqlRequestHandler(
        'farmInputCreditNoteLineItemDelete',
        query,
        variables,
        headers,
      )
        .then((deleted: boolean) => resolve(deleted))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateCreditNoteLineItems(
  input: IFarmInputCreditNoteLineItemInput[],
): Promise<IFarmInputCreditNoteLineItem[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {input};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($input: [FarmInputCreditNoteLineItemInput!]!){
              farmInputCreditNoteLineItemSaveAll(input: $input){
                  id
                  creditNoteId
                  orderId
                  quantity
                  price
                  order{
                      id
                      price
                      quantity
                      product{
                          name
                      }
                  }
              }
          }`;
      graphqlRequestHandler(
        'farmInputCreditNoteLineItemSaveAll',
        query,
        variables,
        headers,
      )
        .then((lineItems: IFarmInputCreditNoteLineItem[]) => resolve(lineItems))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGenerateCreditNotePdf(
  id: number | string,
): Promise<IFarmInputCreditNote> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($id: ID!){
              farmInputCreditNoteGeneratePdf(id: $id){
                  id
                  insertedDate
                  updatedDate
                  invoiceId
                  lineItems{
                    id
                    creditNoteId
                    orderId
                    quantity
                    price
                    order{
                      id
                      price
                      quantity
                      product{
                        name
                      }
                    }
                  }
              }
          }`;
      graphqlRequestHandler(
        'farmInputCreditNoteGeneratePdf',
        query,
        variables,
        headers,
      )
        .then((creditNote: IFarmInputCreditNote) => resolve(creditNote))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetInputPrograms(
  limit?: number,
  offset?: number,
): Promise<IFarmInputProgram[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {limit, offset};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($limit: Int, $offset: Int){
            farmInputPrograms(limit: $limit, offset: $offset){
              id
              createdDate
              updatedDate
              active
              farmerId
              fundingApplication{
              id
              applyingUser{
              id
              externalId
              }
              user{
              id
              }
              progress
              fundingAsk{
              fundingRequestCosts{
              id
              label
              cost
              }
              notes
              expectedFundingYear
              expectedFundingMonth
              }
            }
              farmer{
                id
                userId
                name
                surname
                farm{
                  id
                   farmName
                }
               }
              produceId
              produce{
                id
                name
              }
              type
              status
              hectares
              lines{
                id
                programId
                productId
                product{
                  id
                  name
                  price
                }
                when
                quantity
                PHI
                applicationRate
                applicationUnit
                applicationType
                applicationTypePerHectare
                comment
              }
            }
          }`;
      graphqlRequestHandler('farmInputPrograms', query, variables, headers)
        .then((programs: IFarmInputProgram[]) => resolve(programs))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetInputProgram(
  id: number | string,
): Promise<IFarmInputProgram> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($id: ID!){
            farmInputProgram(id: $id){
              id
              createdDate
              updatedDate
              active
              farmerId
              farmer{
                id
                name
                surname
              }
              produceId
              produce{
                id
                name
              }
              type
              status
              hectares
              lines{
                id
                programId
                productId
                price
                product{
                  id
                  name
                }
                when
                quantity
                PHI
                applicationRate
                applicationUnit
                applicationType
                applicationTypePerHectare
                comment
              }
            }
          }`;
      graphqlRequestHandler('farmInputProgram', query, variables, headers)
        .then((program: IFarmInputProgram) => resolve(program))
        .catch((error) => reject(error));
    });
  });
}

const GET_IRRIGATION_TYPES = `
  query GetGeneralTypes($getIrrigationTypes: Boolean!) {
    getIrrigationTypes @include(if: $getIrrigationTypes) {
      id
      name
      type
      category
    }
  }
`;

export async function apiGetIrrigationTypes(): Promise<IGenericType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {getIrrigationTypes: true};
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getIrrigationTypes',
        GET_IRRIGATION_TYPES,
        variables,
        headers,
      )
        .then((res: IGenericType[]) => resolve(res || []))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetProduce(
  ids?: (number | string)[],
): Promise<IProduce[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {produceIds: ids};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($produceIds: [ID]){
            getAllProduce(produceIds: $produceIds){
              id
              name
              variety{
                id
                name
              }
            }
          }`;
      graphqlRequestHandler('getAllProduce', query, variables, headers)
        .then((allProduce: IProduce[]) => resolve(allProduce))
        .catch((error) => reject(error));
    });
  });
}

export interface IPestDiseaseOption {
  id: string;
  name: string;
}
export async function apiGetPestDiseaseOptions(): Promise<
  IPestDiseaseOption[]
> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const attempts: Array<{operation: string; query: string}> = [
      {
        operation: 'getAllPestsAndDiseases',
        query: `
          query GetAllPestsAndDiseases {
            getAllPestsAndDiseases {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'getAllPestDiseases',
        query: `
          query GetAllPestDiseases {
            getAllPestDiseases {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'pestsAndDiseases',
        query: `
          query GetPestsAndDiseases {
            pestsAndDiseases {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'pestDiseases',
        query: `
          query GetPestDiseases {
            pestDiseases {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'getAllPests',
        query: `
          query GetAllPests {
            getAllPests {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'getAllDiseases',
        query: `
          query GetAllDiseases {
            getAllDiseases {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'allPestsAndDiseases',
        query: `
          query GetAllPestsAndDiseasesAlt {
            allPestsAndDiseases {
              id
              name
            }
          }
        `,
      },
    ];

    return new Promise(async (resolve, reject) => {
      const collectArraysDeep = (value: unknown): unknown[][] => {
        const arrays: unknown[][] = [];
        const visit = (node: unknown, depth: number) => {
          if (depth > 6 || node == null) return;
          if (Array.isArray(node)) {
            arrays.push(node);
            node.forEach((item) => visit(item, depth + 1));
            return;
          }
          if (typeof node === 'object') {
            Object.values(node as Record<string, unknown>).forEach((child) =>
              visit(child, depth + 1),
            );
          }
        };
        visit(value, 0);
        return arrays;
      };

      let lastErrorMessage = '';
      for (const attempt of attempts) {
        try {
          // graphqlRequestHandler extracts `data[name]` when `name` is truthy.
          // Some gateways may not match our guessed field name, so we ask for the
          // whole payload and then pick any array containing `id` + `name`.
          const payload = await graphqlRequestHandler(
            null,
            attempt.query,
            {},
            headers,
          );

          const candidateArrays = collectArraysDeep(payload);

          const options = candidateArrays
            .flat()
            .map((item: any) => {
              if (!item || typeof item !== 'object') return null;

              // Try direct fields
              const directId =
                item.id ??
                item.pestDiseaseId ??
                item.pestsAndDiseasesId ??
                item.pestAndDiseasesId ??
                item.pestAndDiseaseId ??
                item.diseaseId ??
                item.pestId;

              const directName =
                item.name ??
                item.pestDiseaseName ??
                item.pestsAndDiseasesName ??
                item.pestAndDiseasesName ??
                item.pestAndDiseaseName ??
                item.diseaseName ??
                item.pestName ??
                item.title;

              // Try common nesting patterns
              const nested =
                item.pestDisease?.id != null || item.pestDisease?.name
                  ? {id: item.pestDisease.id, name: item.pestDisease.name}
                  : item.pestsAndDiseases?.id != null ||
                    item.pestsAndDiseases?.name
                  ? {
                      id: item.pestsAndDiseases.id,
                      name: item.pestsAndDiseases.name,
                    }
                  : item.pestAndDisease?.id != null || item.pestAndDisease?.name
                  ? {id: item.pestAndDisease.id, name: item.pestAndDisease.name}
                  : item.pestDiseases?.id != null || item.pestDiseases?.name
                  ? {id: item.pestDiseases.id, name: item.pestDiseases.name}
                  : null;

              const id = nested?.id ?? directId;
              const name = (nested?.name ?? directName) as unknown;

              if (id == null) return null;

              return {
                id: String(id),
                name:
                  typeof name === 'string' && name.trim()
                    ? name.trim()
                    : `Pest/Disease ${String(id)}`,
              };
            })
            .filter(Boolean) as Array<{id: string; name: string}>;

          // De-dupe by id
          const seen = new Set<string>();
          const deduped = options.filter((o) => {
            if (seen.has(o.id)) return false;
            seen.add(o.id);
            return true;
          });

          if (deduped.length) {
            resolve(deduped);
            return;
          }
        } catch (error) {
          const raw = Array.isArray(error)
            ? error.join('; ')
            : String((error as any)?.message || error);
          lastErrorMessage = raw;
          // Try next query variant for schema compatibility.
        }
      }
      reject(
        new Error(lastErrorMessage || 'Failed to load pest/disease options'),
      );
    });
  });
}

export async function apiGetAllFdApplications(): Promise<IFdApplicationType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query{
            getAllFDApplications{
              id
              createdAt
              applicationState
              completedSections
              incompleteSections
              completedSectionsPercentage
              applyingUser{
                id
                externalId
                name
                surname
                email
                phoneNumber
                creditScore
              }
              company{
                id
                name
              }
              farm{
                id
                name
                numberOfLeasedHectares
                ownedHectares
                irrigatedHectares
                drylandHectares
                province
              }
              progress
              createdAt
              completed
              farmAsset{
                id
              }
              fundingAsk{
                fundingRequestCosts{
                  id
                  label
                  cost
                }
                notes
                expectedFundingYear
                expectedFundingMonth
              }
              productionPlans{
                id
                crop{
                  id
                  name
                }
              }
              applicationOwnerships{
                id
                owner{
                  id
                  name
                  surname
                  pictureUrl
                }
                ownerId
                reportingTo{
                  id
                  name
                  surname
                  pictureUrl
                }
                reportingToId
                notes
                state
                createdAt
                updatedAt
              }
            }
          }`;
      graphqlRequestHandler('getAllFDApplications', query, variables, headers)
        .then((applications: IFdApplicationType[]) => resolve(applications))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdApplication(
  id: number | string,
): Promise<IFdApplicationType | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($id: Float!){
            getFDApplicationById(id: $id){
              id
              createdAt
              applicationWallet
              applicationWalletAccount {
                id
                customName
                accounts {
                  id
                  accountName
                  accountNumber
                  AvailableBalance
                }
              }
              applicationState
              completedSections
              incompleteSections
              completedDocTypesPercentage
              incompleteDocTypes
              completedSectionsPercentage
              applyingUser{
                id
                name
                surname
                email
                phoneNumber
                creditScore
                externalId
                idNumber
              }
              company{
                id
                name
              }
              farm{
                id
                name
                description
                directions
                farmArrangement
                numberOfLeasedHectares
                ownedHectares
                irrigatedHectares
                drylandHectares
                address
                borehole
                dam
                river
                waterRights
                fullTimeFarming
                leaseEndDate
                shortTermGoals
                longTermGoals
                averageRainfall
                landComposition
                workforceNumber
                workerTrust
                employmentBeforeAndAfter
                province
              }
              progress
              createdAt
              completed
              farmAsset{
                id
              }
              fundingAsk{
                fundingRequestCosts{
                  id
                  label
                  cost
                }
                notes
                expectedFundingYear
                expectedFundingMonth
              }
              productionPlans{
                id
                crop{
                  id
                  name
                }
                numberOfHectares
                estimatedYield
                estimatedPrice
                plantingDate
                irrigation
                plantPopulation
                notes
                areaOfDryLand
                areaUnderIrrigation
              }
              team{
                id
                name
                farmTeamRole{
                  id
                  name
                  description
                }
              }
              customers{
                id
                crop{
                  id
                  name
                }
                customer{
                  id
                  name
                }
              }
              suppliers{
                id
                supplierList{
                  id
                  name
                  products{
                    id
                    name
                  }
                }
              }
              farmRisk{
                id
                risk{
                  id
                  name
                }
                problem
                mitigation
              }
              financialPerformance{
                id
                year
                revenue
                expenses
                operatingCosts
                profit
                financialYearEnd
                financialYearEndMonth
                notes
              }
              productionCosts{
                id
                crop{
                  id
                  name
                }
                plantingDate
                year
                seed
                fertiliser
                chemical
                numberOfHectares
                notes
              }
              operatingCosts{
                id
                year
                monthlyCosts
                notes
                expense{
                  id
                  name
                }
                liability{
                  id
                  name
                }
              }
              assets{
                id
                description
                model
                make
                year
                value
                condition
                pricePerUnit
                capacity
                assetType
                farm{
                  id
                }
                assetListItem{
                  id
                  name
                  assetCategory        
                }
              }
              # liabilities{
              #   id
              #   lender
              #   creditLimitAmount
              #   currentBalanceLimit
              #   monthlyInstallmentAmount
              #   inArrears
              #   liabilityAccountType{
              #     id
              #     name
              #   }
              # }
              applicationDocuments{
                id
                documentType
                filePath
                createdAt
                updatedAt
              }
              productionHistory{
                id
                crop{
                  id
                  name
                }
                year
                numberOfHectaresFarmed
                yieldPerHectare
                notes
                plantYear
                plantMonth
                harvestYear
                harvestMonth
                farm{
                  id
                }
              }
              applicationLivestock{
                id
                livestock{
                  id
                  name
                  livestockType
                  livestockProduct
                }
                quantity
                type
                productGrading
                sellOfPeriod
                price
                notes
              }
              applicationWaterRights{
                id
                waterSource
                householdConsumption
                notes
                waterRights
                waterScale
                permitNumber
                controlled
                waterQuantity
                scheme
                schedule
              }
              applicationLongTermCrops{
                id
                crop{
                  id
                  name
                }
                blockNumber
                age
                numberOfTrees
                area
                trellies
                spacing
                irrigation
                type
                notes
                projectedYear
                projectedYield
                projectedPrice
                projectedArea
                historyYear1
                historyYield1
                historyArea1
                historyYear2
                historyYield2
                historyArea2
                historyYear3
                historyYield3
                historyArea3
              }
              applicationProperties{
                id
                nameOfDeed
                areaDistrict
                farmArrangement
                term
                amount
                dryland
                irrigation
                pastures
                grazing
                intensive
                other
                notes
                total
              }
            }
          }`;
      graphqlRequestHandler('getFDApplicationById', query, variables, headers)
        .then((applications: IFdApplicationType) => resolve(applications))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetUserFdApplications(
  externalUserId: number | string,
): Promise<IFdApplicationType[] | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {externalUserId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($externalUserId: ID!){
            getFDApplicationsByExternalUser(externalUserId: $externalUserId){
              id
              createdAt
              applicationState
              fundingAsk{
              fundingRequestCosts{
                  id
                  label
                  cost
                }
                notes
                expectedFundingYear
                expectedFundingMonth
              }
            }
          }`;
      graphqlRequestHandler(
        'getFDApplicationsByExternalUser',
        query,
        variables,
        headers,
      )
        .then((applications: IFdApplicationType[]) => resolve(applications))
        .catch((error) => reject(error));
    });
  });
}

export async function apiInitiateFdApplication(input: {
  applyingUser: string | number;
  farm: string | number;
  company: string | number;
}): Promise<{id: string}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {
      applyingUser: Number(input.applyingUser),
      farm: Number(input.farm),
      company: Number(input.company),
    };
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($applyingUser: ID!, $farm: Float!, $company: Float!){
            createFDSupplierApplication(applicationData: {
              applyingUser: $applyingUser,
              farm: $farm,
              company: $company
            }){
              id
            }
          }`;
      graphqlRequestHandler(
        'createFDSupplierApplication',
        query,
        variables,
        headers,
      )
        .then((result: any) =>
          resolve(result?.createFDSupplierApplication ?? {id: ''}),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFundingUserFromExternal(
  userId: number | string,
): Promise<IFdUser | null> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {userId: Number(userId)};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($userId: Float!){
            FDGetUserFromExternal(userId: $userId){
              id
              externalId
              name
              surname
              email
              phoneNumber
              creditScore
              company{
                id
                name
              }
              farm{
                id
                name
                province
                numberOfLeasedHectares
                ownedHectares
                irrigatedHectares
                drylandHectares
              }
            }
          }`;
      graphqlRequestHandler('FDGetUserFromExternal', query, variables, headers)
        .then((user: IFdUser) => resolve(user || null))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdGetUser(
  id: number | string,
): Promise<IFdUser | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($id: Float!){
            FDGetUserById(id: $id){
              id
              name
              surname
              email
              phoneNumber
              creditScore
              company{
                id
                name
              }
              farm{
                id
                name
                numberOfLeasedHectares
                ownedHectares
                irrigatedHectares
                drylandHectares
              }
            }
          }`;
      graphqlRequestHandler('FDGetUserById', query, variables, headers)
        .then((user: IFdUser) => resolve(user || null))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdUserByExternalId(
  externalId: string,
): Promise<IFdUser | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {externalId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($externalId: String!){
            FDGetUserByExternalId(externalId: $externalId){
              id
              name
              surname
              email
              phoneNumber
              company{
                id
                name
                address
              }
              farm{
                id
                name
                province
                address
                ownedHectares
              }
            }
          }`;
      graphqlRequestHandler('FDGetUserByExternalId', query, variables, headers)
        .then((user: IFdUser) => resolve(user || null))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdUserByEmail(
  email: string,
): Promise<IFdUser | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {email};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($email: String!){
            FDGetUserByEmail(email: $email){
              id
              name
              surname
              email
              phoneNumber
              company{
                id
                name
                address
              }
              farm{
                id
                name
                province
                address
                ownedHectares
              }
              financials{
                vatNumber
                taxNumber
              }
            }
          }`;
      graphqlRequestHandler('FDGetUserByEmail', query, variables, headers)
        .then((user: IFdUser) => resolve(user || null))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdUser(userData: {
  id: number;
  name?: string;
  surname?: string;
  email?: string;
  phoneNumber?: string;
}): Promise<IFdUser> {
  return lshGETAUTH().then((data) => {
    const headers: Record<string, string> = {Authorization: data};
    const variables = {userData};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($userData: UpdateUserDto!){
            updateFDUser(userData: $userData){
              id
              name
              surname
              email
              phoneNumber
            }
          }`;
      graphqlRequestHandler('updateFDUser', query, variables, headers)
        .then((user: IFdUser) => resolve(user))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdCompany(companyDetails: {
  id: number;
  name?: string;
  address?: string;
  email?: string;
  phoneNumber?: string;
}): Promise<IFdCompany> {
  return lshGETAUTH().then((data) => {
    const headers: Record<string, string> = {Authorization: data};
    const variables = {companyDetails};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($companyDetails: UpdateCompanyDTO!){
            updateFDCompany(companyDetails: $companyDetails){
              id
              name
              address
            }
          }`;
      graphqlRequestHandler('updateFDCompany', query, variables, headers)
        .then((company: IFdCompany) => resolve(company))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdFarm(farmData: {
  farmId?: number;
  applyingUserId?: number;
  name?: string;
  province?: string;
  address?: string;
  ownedHectares?: number;
}): Promise<IFdFarm> {
  return lshGETAUTH().then((data) => {
    const headers: Record<string, string> = {Authorization: data};
    const variables = {farmData};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($farmData: UpdateFarmDTO!){
            updateFDFarm(farmData: $farmData){
              id
              name
              province
              address
              ownedHectares
            }
          }`;
      graphqlRequestHandler('updateFDFarm', query, variables, headers)
        .then((farm: IFdFarm) => resolve(farm))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdFarmFinancials(farmFinancialsData: {
  companyId: number;
  vatNumber?: string;
  taxNumber?: string;
  bankName?: string;
  accountNumber?: string;
}): Promise<{
  id: number;
  vatNumber: string;
  taxNumber: string;
  bankName: string;
  accountNumber: string;
}> {
  return lshGETAUTH().then((data) => {
    const headers: Record<string, string> = {Authorization: data};
    const variables = {farmFinancialsData};
    return new Promise((resolve, reject) => {
      const query = `
          mutation variables($farmFinancialsData: UpdateFarmFinancialsDTO!){
            updateFDFarmFinancials(farmFinancialsData: $farmFinancialsData){
              id
              vatNumber
              taxNumber
              bankName
              accountNumber
            }
          }`;
      graphqlRequestHandler('updateFDFarmFinancials', query, variables, headers)
        .then((result: any) => resolve(result))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetUserTraderApplications(
  userId: number | string,
): Promise<ITraderApplication[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {userId};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($userId: ID!){
            getUserTraderApplications(userId: $userId){
              id
              tradeType
              company{
                id
                name
                tradingName
                vatNumber
                taxNumber
                addressLine1
                addressLine2
                city
                province
                bank{
                  id
                  name
                }
                bankAccountType{
                  id
                  name
                }
                bankBranchCode
                bankAccountNumber
              }
              farmer{
              farm{
              beeScore
              }
              }
              forms{
                id
                name
                description
                isComplete
                sections{
                  id
                  name
                  isComplete
                  fields{
                    id
                    dependsOn{
                      fieldId
                      values
                    }
                    groupBy
                    __typename
                    ... on TradingFormText{
                      textTitle: title
                      textBody: body
                      textType: type
                      groupView
                    }
                    ... on TradingFormTextInput{
                      title
                      value
                      textInputTitle: title
                      textInputValue: value
                      textInputValidationTypes: validationTypes
                      isOptional
                    }
                    ... on TradingFormSingleChoiceOption{
                      singleChoiceOptionTitle: title
                      singleChoiceOptionValue: value
                      singleChoiceOptionType: type
                      singleChoiceOptionOptions: options{
                        title
                        value
                      }
                    }
                    ... on TradingFormMultiChoiceOption{
                      multiChoiceOptionTitle: title
                      multiChoiceOptionValues: values
                      multiChoiceOptionOptions: options{
                        title
                        value
                      }
                    }
                    ... on TradingFormCheckBox{
                      checkBoxTitle: title
                      checkBoxValue: value
                    }
                    ... on TradingFormFileUpload{
                      fileUploadTitle: title
                      fileUploadSubTitle: subTitle
                      fileUploadIsOptional: isOptional
                      fileUploadState: state
                      fileUploadValue: value
                      allowMultiple
                    }
                  }
                }
              }
            }
          }`;
      graphqlRequestHandler(
        'getUserTraderApplications',
        query,
        variables,
        headers,
      )
        .then((applications: ITraderApplication[]) => resolve(applications))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetTraderForm(
  formId: number | string,
  userId?: number | string,
): Promise<ITradingForm | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables: Record<string, unknown> = {
      formId,
      companyEntityType: true,
    };
    if (userId != null) variables.userId = userId;
    return new Promise((resolve, reject) => {
      const query = `
          query variables($formId: ID!, $companyEntityType: Boolean, $userId: ID){
            getTradingForm(formId: $formId, companyEntityType: $companyEntityType, userId: $userId){
              id
              name
              description
              isComplete
              sections{
                id
                name
                isComplete
                fields{
                  id
                  dependsOn{
                    fieldId
                    values
                  }
                  groupBy
                  __typename
                  ... on TradingFormText{
                    textTitle: title
                    textBody: body
                    textType: type
                    groupView
                  }
                  ... on TradingFormTextInput{
                    textInputTitle: title
                    textInputValue: value
                    textInputValidationTypes: validationTypes
                    isOptional
                  }
                  ... on TradingFormSingleChoiceOption{
                    singleChoiceOptionTitle: title
                    singleChoiceOptionValue: value
                    singleChoiceOptionType: type
                    singleChoiceOptionOptions: options{
                      title
                      value
                    }
                  }
                  ... on TradingFormMultiChoiceOption{
                    multiChoiceOptionTitle: title
                    multiChoiceOptionValues: values
                    multiChoiceOptionOptions: options{
                      title
                      value
                    }
                  }
                  ... on TradingFormCheckBox{
                    checkBoxTitle: title
                    checkBoxValue: value
                  }
                  ... on TradingFormFileUpload{
                    fileUploadTitle: title
                    fileUploadSubTitle: subTitle
                    fileUploadIsOptional: isOptional
                    fileUploadState: state
                    fileUploadValue: value
                    allowMultiple
                  }
                }
              }
            }
          }`;
      graphqlRequestHandler('getTradingForm', query, variables, headers)
        .then((form: ITradingForm) => resolve(form || null))
        .catch((error) => reject(error));
    });
  });
}

export interface ISubmitTraderFormFieldInput {
  id: string;
  value: string;
  fieldTypeName: string;
  isOptional?: boolean;
}

export interface ISubmitTraderFormFieldsResponse {
  success: boolean;
  message: string;
}

export async function apiSubmitTraderFormFields(input: {
  applicationId: number | string;
  fields: ISubmitTraderFormFieldInput[];
  companyEntityType?: boolean;
}): Promise<ISubmitTraderFormFieldsResponse> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {
      applicationId: input.applicationId,
      fields: input.fields,
      companyEntityType: input.companyEntityType ?? true,
    };

    return new Promise((resolve, reject) => {
      const query = `
        mutation variables(
          $applicationId: ID!
          $fields: [TradingFormFieldInput!]!
          $companyEntityType: Boolean
        ) {
          submitTraderFormFields(
            applicationId: $applicationId
            fields: $fields
            companyEntityType: $companyEntityType
          ) {
            success
            message
          }
        }`;

      graphqlRequestHandler('submitTraderFormFields', query, variables, headers)
        .then((response: ISubmitTraderFormFieldsResponse) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiFarmerLinkedAccounts(
  farmerId: string,
): Promise<ILinkedGpayAccount[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    // Provide farmerId as a variable
    const variables = {farmerId};

    // Include ($farmerId: ID!) in the query signature
    // Then pass farmerId into getFarmerInputsLinkedAccounts(farmerId: $farmerId)
    const query = `
      query getFarmerInputsLinkedAccounts($farmerId: ID!) {
        getFarmerInputsLinkedAccounts(farmerId: $farmerId) {
          id
          company {
            id
            name
            image
          }
          accounts {
            id
            accountType
            accountName
            AccountBalance
            AvailableBalance
            withdrawable
            accountNumber
          }
          withdrawableAmounts {
            id
            transactionName
            amount
            availableBalance
            withdrawalRequests {
              id
              transactionName
              amount
              approved
            }
          }
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getFarmerInputsLinkedAccounts',
        query,
        variables,
        headers,
      )
        .then((response: any) => {
          // Adjust based on how your handler returns data:
          // e.g. if response = { data: { getFarmerInputsLinkedAccounts: ... } }
          // you might do:
          // resolve(response.data.getFarmerInputsLinkedAccounts);

          resolve(response);
        })
        .catch((error: any) => reject(error));
    });
  });
}

export async function apiGetAccountStatement(
  gpayId: string,
  accountType: EGPAY_ACCOUNT_TYPE,
  startDate?: string,
  endDate?: string,
): Promise<IGpayAccount> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getFarmerInputsWalletAccountStatement
      ($gpayId: ID, $period: StatementPeriodInput, $accountType: ACCOUNT_TYPE) {
        getFarmerInputsWalletAccountStatement(gpayId: $gpayId, period: $period, accountType: $accountType) {
          accountName
          AccountBalance
          AvailableBalance
          Statement {
            TransactionDate
            SyncDate
            Description
            Reference
            SupplierBankReference
            Amount
            InvoiceNumber
          }
        }
      }
    `;

    // GraphQL variables
    const variables = {
      gpayId,
      accountType,
      period: {
        startDate: startDate,
        endDate: endDate,
      },
    };

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getFarmerInputsWalletAccountStatement',
        query,
        variables,
        headers,
      )
        .then((response: any) => {
          if (response?.data?.getFarmerInputsWalletAccountStatement) {
            resolve(response.data.getFarmerInputsWalletAccountStatement);
          } else {
            resolve(response);
          }
        })
        .catch((error: any) => reject(error));
    });
  });
}

export async function apiCreateAccountRestriction(
  accountId: string,
  categoryId: string,
  amount: number,
): Promise<{success: boolean; message: string}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const mutation = `
      mutation createAccountRestriction($accountId: ID!, $categoryId: ID!, $amount: Float!) {
        createAccountRestriction(accountId: $accountId, categoryId: $categoryId, amount: $amount) {
          success
          message
        }
      }
    `;

    // GraphQL variables
    const variables = {
      accountId,
      categoryId,
      amount,
    };

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createAccountRestriction',
        mutation,
        variables,
        headers,
      )
        .then((response: any) => {
          if (response?.data?.createAccountRestriction) {
            // e.g. { success: true, message: "Restriction created" }
            resolve(response.data.createAccountRestriction);
          } else {
            // fallback if something unexpected
            resolve(response);
          }
        })
        .catch((error: any) => reject(error));
    });
  });
}

export async function apiGetFdCrops(): Promise<IFdCrop[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
          query {
            getAllFDCrops{
              id
              name
              active
            }
          }`;
      graphqlRequestHandler('getAllFDCrops', query, variables, headers)
        .then((crops: IFdCrop[]) => resolve(crops || []))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFarm(farmData: IUpdateFdFarm): Promise<IFdFarm> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {farmData};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variables($farmData: UpdateFarmDTO!) {
          updateFDFarm(farmData: $farmData) {
            id
            name
            address
            description
            directions
            numberOfLeasedHectares
            leaseEndDate
            ownedHectares
            farmArrangement
            fullTimeFarming
            irrigatedHectares
            drylandHectares
            borehole
            dam
            river
            waterRights
            shortTermGoals
            longTermGoals
            landComposition
            workforceNumber
            workerTrust
            employmentBeforeAndAfter
          }
        }`;
      graphqlRequestHandler('updateFDFarm', query, variables, headers)
        .then((farm: IFdFarm) => resolve(farm))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdProductionPlans(
  productionPlansData: ISaveFdProductionPlanDto[],
): Promise<IFdProductionPlan[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {productionPlansData};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variables(
          $productionPlansData: [SaveProductionPlanDTO!]!
        ) {
          createFDAddProductionPlans(productionPlansData: $productionPlansData) {
            id
            crop {
              id
              name
            }
            numberOfHectares
            estimatedYield
            plantingDate
            notes
            irrigation
            plantPopulation
            estimatedPrice
            areaOfDryLand
            areaUnderIrrigation
            harvestYear
            harvestMonth
          }
        }`;
      graphqlRequestHandler(
        'createFDAddProductionPlans',
        query,
        variables,
        headers,
      )
        .then((productionPlans: IFdProductionPlan[]) =>
          resolve(productionPlans),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdProductionPlansByApplicationId(
  applicationId: string | number,
): Promise<IFdProductionPlan[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: Number(applicationId)};

    return new Promise((resolve, reject) => {
      const query = `
        query getFDProductionPlansByApplictionId($id: Float!) {
          getFDProductionPlansByApplictionId(id: $id) {
            id
            crop {
              id
              name
            }
            numberOfHectares
            estimatedYield
            plantingDate
            notes
            irrigation
            plantPopulation
            estimatedPrice
            areaOfDryLand
            areaUnderIrrigation
            harvestYear
            harvestMonth
          }
        }`;

      graphqlRequestHandler(
        'getFDProductionPlansByApplictionId',
        query,
        variables,
        headers,
      )
        .then((productionPlans: IFdProductionPlan[]) =>
          resolve(productionPlans || []),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFdProductionPlan(
  id: string | number,
): Promise<{id: number}> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: Number(id)};

    return new Promise((resolve, reject) => {
      const mutation = `
        mutation deleteFDProductionPlan($id: Float!) {
          deleteFDProductionPlan(id: $id) {
            id
          }
        }`;

      graphqlRequestHandler(
        'deleteFDProductionPlan',
        mutation,
        variables,
        headers,
      )
        .then((response: {id: number}) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export interface IFdProjectedRevenueWindow {
  startMonth: string;
  endMonth: string;
}

export interface IFdProjectedRevenueLookup {
  id: string;
  cropId: string;
  cropName: string;
  year: number;
  monthlyCosts: string | null;
  window: IFdProjectedRevenueWindow;
}

interface IProjectedRevenueRecord {
  id: string | number;
  monthlyCosts: string | null;
  year: number;
  crop?: {
    id?: string | number;
    name?: string;
  } | null;
}

function lshParseProjectedRevenueWindow(
  raw: string | null | undefined,
): IFdProjectedRevenueWindow | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    return {
      startMonth: String((parsed as any).startMonth ?? ''),
      endMonth: String((parsed as any).endMonth ?? ''),
    };
  } catch (error) {
    console.error('Failed to parse projected revenue monthlyCosts', error);
    return null;
  }
}

export async function apiGetFdProjectedRevenuesByApplicationId(
  applicationId: string | number,
): Promise<IFdProjectedRevenueLookup[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: Number(applicationId)};

    return new Promise((resolve, reject) => {
      const query = `
        query getFDProjectedRevenuesByApplicationId($id: Float!) {
          getFDProjectedRevenuesByApplicationId(id: $id) {
            id
            monthlyCosts
            year
            crop {
              id
              name
            }
          }
        }`;

      graphqlRequestHandler(
        'getFDProjectedRevenuesByApplicationId',
        query,
        variables,
        headers,
      )
        .then((records: IProjectedRevenueRecord[]) =>
          resolve(
            (records || []).map((record) => ({
              id: String(record.id),
              cropId: record.crop?.id ? String(record.crop.id) : '',
              cropName: record.crop?.name || 'Unknown crop',
              year: Number(record.year ?? 0),
              monthlyCosts: record.monthlyCosts,
              window: lshParseProjectedRevenueWindow(record.monthlyCosts) || {
                startMonth: '',
                endMonth: '',
              },
            })),
          ),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpsertFdProjectedRevenue(input: {
  id?: string;
  monthlyCosts: IFdProjectedRevenueWindow;
  year: number;
  cropId: number;
  applicationId: number;
  notes?: string;
}): Promise<IFdProjectedRevenueLookup | null> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {
      createProjectedRevenueData: {
        ...(input.id ? {id: input.id} : {}),
        monthlyCosts: JSON.stringify(input.monthlyCosts),
        year: input.year,
        cropId: input.cropId,
        applicationId: input.applicationId,
        notes: input.notes,
      },
    };

    return new Promise((resolve, reject) => {
      const mutation = `
        mutation createFDProjectedRevenue(
          $createProjectedRevenueData: CreateProjectedRevenueDTO!
        ) {
          createFDProjectedRevenue(
            createProjectedRevenueData: $createProjectedRevenueData
          ) {
            id
            monthlyCosts
            year
            crop {
              id
              name
            }
          }
        }`;

      graphqlRequestHandler(
        'createFDProjectedRevenue',
        mutation,
        variables,
        headers,
      )
        .then((record: IProjectedRevenueRecord | null) => {
          if (!record) {
            resolve(null);
            return;
          }

          resolve({
            id: String(record.id),
            cropId: record.crop?.id ? String(record.crop.id) : '',
            cropName: record.crop?.name || 'Unknown crop',
            year: Number(record.year ?? 0),
            monthlyCosts: record.monthlyCosts,
            window: lshParseProjectedRevenueWindow(record.monthlyCosts) || {
              startMonth: '',
              endMonth: '',
            },
          });
        })
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdProductionHistory(
  productionHistoryData: ISaveFdProductionHistory,
): Promise<IFdProductionHistory> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {productionHistoryData};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variable($productionHistoryData: ProductionHistoryDTO!) {
          createFDProductionHistory(productionHistoryData: $productionHistoryData) {
            id
            crop {
              id
              name
            }
            numberOfHectaresFarmed
            yieldPerHectare
            year
            notes
            plantYear
            plantMonth
            harvestYear
            harvestMonth
          }
        }
      `;
      graphqlRequestHandler(
        'createFDProductionHistory',
        query,
        variables,
        headers,
      )
        .then((productionHistory: IFdProductionHistory) =>
          resolve(productionHistory),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdProductionHistoryByApplicationId(
  applicationId: number | string,
): Promise<IFdProductionHistory[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: Number(applicationId)};

    return new Promise((resolve, reject) => {
      const query = `
        query getFDProductionHistoryByApplicationId($id: Float!) {
          getFDProductionHistoryByApplicationId(id: $id) {
            id
            crop {
              id
              name
            }
            numberOfHectaresFarmed
            yieldPerHectare
            year
            notes
            plantYear
            plantMonth
            harvestYear
            harvestMonth
          }
        }`;

      graphqlRequestHandler(
        'getFDProductionHistoryByApplicationId',
        query,
        variables,
        headers,
      )
        .then((records: IFdProductionHistory[]) => resolve(records || []))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFdProductionHistory(
  id: string | number,
): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id: String(id)};

    return new Promise((resolve, reject) => {
      const mutation = `
        mutation deleteFDProductionHistory($id: ID!) {
          deleteFDProductionHistory(id: $id)
        }`;

      graphqlRequestHandler(
        'deleteFDProductionHistory',
        mutation,
        variables,
        headers,
      )
        .then((response: boolean) => resolve(Boolean(response)))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdFinancialPerformance(
  financialPerformanceData: ISaveFdFinancialPerformance,
): Promise<IFdFinancialPerformance> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {financialPerformanceData};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variable($financialPerformanceData: CreateFinancialPerformanceDTO!) {
          upsertFDFinancialPerformance(financialPerformanceData: $financialPerformanceData) {
            id
            year
            revenue
            expenses
            operatingCosts
            profit
            financialYearEnd
            financialYearEndMonth
            notes
          }
        }
      `;
      graphqlRequestHandler(
        'upsertFDFinancialPerformance',
        query,
        variables,
        headers,
      )
        .then((financialPerformance: IFdFinancialPerformance) =>
          resolve(financialPerformance),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiCreateFdApplicationDoc(
  applicationDocData: ISaveFdDoc,
): Promise<IFdApplicationDocType> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {applicationDocData};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variables($applicationDocData: CreateApplicationDocDTO!) {
          FDCreateApplicationDoc(applicationDocData: $applicationDocData) {
            id
            documentType
            filePath
            createdAt
            updatedAt
          }
        }
      `;
      graphqlRequestHandler('FDCreateApplicationDoc', query, variables, headers)
        .then((doc: IFdApplicationDocType) => resolve(doc))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFdApplicationDoc(
  docId: number,
): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {docId};
    return new Promise((resolve, reject) => {
      const query = `
        mutation variables($docId: Float!) {
          FDDeleteApplicationDoc(docId: $docId)
        }
      `;
      graphqlRequestHandler('FDDeleteApplicationDoc', query, variables, headers)
        .then((deleted: boolean) => resolve(deleted))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpdateFdApplicationProgress(input: {
  applicationId: number;
  completed: boolean;
  progress: number;
  subProgress: number;
  state?: EApplicationStateType;
}): Promise<IFdApplicationType> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {...input};

    const mutation = `
      mutation updateFDApplicationProgress(
        $applicationId: Float!
        $completed: Boolean!
        $progress: Float!
        $subProgress: Float
        $state: EApplicationStateType
      ) {
        updateFDApplicationProgress(
          applicationId: $applicationId
          completed: $completed
          progress: $progress
          subProgress: $subProgress
          state: $state
        ) {
          id
          completed
          subProgress
          progress
          applicationState
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'updateFDApplicationProgress',
        mutation,
        variables,
        headers,
      )
        .then((application: IFdApplicationType) => resolve(application))
        .catch(reject);
    });
  });
}

export async function apiGetFdAgreementTerms(): Promise<IFdAgreementDocType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const query = `
      query getFDAgreementTerms {
        getFDAgreementTerms {
          id
          agreement
          agreementType
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getFDAgreementTerms', query, {}, headers)
        .then((terms: IFdAgreementDocType[]) => resolve(terms || []))
        .catch(reject);
    });
  });
}

export async function apiGetFdApplicationAgreementsByApplicationId(
  applicationId: number,
): Promise<IFdApplicationAgreementType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId};

    const query = `
      query getFDApplicationAgreementsByApplicationId($applicationId: Float!) {
        getFDApplicationAgreementsByApplicationId(applicationId: $applicationId) {
          id
          signatureUrl
          createdAt
          agreementDoc {
            id
            agreement
            agreementType
          }
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getFDApplicationAgreementsByApplicationId',
        query,
        variables,
        headers,
      )
        .then((agreements: IFdApplicationAgreementType[]) =>
          resolve(agreements || []),
        )
        .catch(reject);
    });
  });
}

export async function apiCreateFdApplicationAgreement(input: {
  applicationId: number;
  agreementDocId: number;
  signature: string;
}): Promise<IFdApplicationAgreementType> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {...input};

    const mutation = `
      mutation createFDApplicationAgreement(
        $applicationId: Float!
        $agreementDocId: Float!
        $signature: String!
      ) {
        createFDApplicationAgreement(
          applicationId: $applicationId
          agreementDocId: $agreementDocId
          signature: $signature
        ) {
          id
          signatureUrl
          createdAt
          agreementDoc {
            id
            agreement
            agreementType
          }
          application {
            id
            progress
            completed
            applicationState
          }
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createFDApplicationAgreement',
        mutation,
        variables,
        headers,
      )
        .then((agreement: IFdApplicationAgreementType) => resolve(agreement))
        .catch(reject);
    });
  });
}

export async function apiGetFdOperatingExpenses(): Promise<IGenericType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
        query{
          getAllFDExpenses {
              id
              name
            }
        }
      `;
      graphqlRequestHandler('getAllFDExpenses', query, variables, headers)
        .then((expenses: IGenericType[]) => resolve(expenses))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpsertFdOperatingCost(
  operatingCostData: ISaveFdOperatingCost,
): Promise<IFdOperatingCost> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {operatingCostData};
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation upsertFDOperatingCost($operatingCostData: CreateOperatingCostDTO!) {
          upsertFDOperatingCost(operatingCostData: $operatingCostData) {
            id
            notes
          }
        }
      `;
      graphqlRequestHandler(
        'upsertFDOperatingCost',
        mutation,
        variables,
        headers,
      )
        .then((result: IFdOperatingCost) => resolve(result))
        .catch((error) => reject(error));
    });
  });
}

// Delete an Operating Cost row by id
export async function apiDeleteFdOperatingCost(id: number): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation deleteFDOperatingCost($id: Float!) {
          deleteFDOperatingCost(id: $id)
        }
      `;
      graphqlRequestHandler(
        'deleteFDOperatingCost',
        mutation,
        variables,
        headers,
      )
        .then((deleted: boolean) => resolve(deleted))
        .catch((error) => reject(error));
    });
  });
}

// Delete a Farm Asset by id
export async function apiDeleteFarmAsset(id: number): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    return new Promise((resolve, reject) => {
      const mutation = `
        mutation deleteFDFarmAsset($id: Float!) {
          deleteFDFarmAsset(id: $id)
        }
      `;
      graphqlRequestHandler('deleteFDFarmAsset', mutation, variables, headers)
        .then((deleted: boolean) => resolve(deleted))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdAssets(): Promise<IFdFarmAssetList[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
        query{
          getAllFDFarmAssetList {
              id
              name
              assetCategory
              assetUnit
              description
          }
        }
      `;
      graphqlRequestHandler('getAllFDFarmAssetList', query, variables, headers)
        .then((assets: IFdFarmAssetList[]) => resolve(assets))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpsertFarmAsset(
  assetData: ISaveFdFarmAsset,
): Promise<IFdFarmAssetList> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {assetData};
    const mutation = `
      mutation upsertFarmAsset($assetData: FarmAssetDTO!) {
        upsertFarmAsset(assetData: $assetData) {
          id
          description
          year
          condition
          value
          pricePerUnit
          model
          make
          capacity
          assetType
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('upsertFarmAsset', mutation, variables, headers)
        .then((result: IFdFarmAssetList) => resolve(result))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetProgramsByFundingApplication(
  applicationId: string | number,
): Promise<IFarmInputProgram[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId};

    const query = `
      query GetProgramsByFundingApplication($applicationId: ID!) {
        findAllByFundingApplicationId(applicationId: $applicationId) {
          id
          createdDate
          updatedDate
          active
          type
          status
          hectares

          produce {
            id
            name
          }
          farmer {
            id
            name
            surname
          }
          lines {
            id
            product {
              name
              price
            }
            quantity
          }
        }
      }
    `;

    return new Promise<IFarmInputProgram[]>((resolve, reject) => {
      graphqlRequestHandler(
        'findAllByFundingApplicationId', // <- root field to extract
        query,
        variables,
        headers,
      )
        .then((programs: IFarmInputProgram[]) => resolve(programs))
        .catch((err) => reject(err));
    });
  });
}

export async function apiLinkProgramToFundingApplication(
  programId: string | number,
  applicationId: string | number,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {programId, applicationId};

    const mutation = `
      mutation LinkProgramToFundingApplication($programId: ID!, $applicationId: ID!) {
        linkProgramToFundingApplication(
          programId: $programId
          applicationId: $applicationId
        ) {
          success
          message
        }
      }
    `;

    return new Promise<IGenericResponse>((resolve, reject) => {
      graphqlRequestHandler(
        'linkProgramToFundingApplication', // <- root field to extract
        mutation,
        variables,
        headers,
      )
        .then((resp: IGenericResponse) => resolve(resp))
        .catch((err) => reject(err));
    });
  });
}

export async function apiUnlinkProgramFromFundingApplication(
  programId: string | number,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {programId};

    const mutation = `
      mutation UnlinkProgramFromFundingApplication($programId: ID!) {
        unlinkProgramFromFundingApplication(programId: $programId) {
          success
          message
        }
      }
    `;

    return new Promise<IGenericResponse>((resolve, reject) => {
      graphqlRequestHandler(
        'unlinkProgramFromFundingApplication', // root field
        mutation,
        variables,
        headers,
      )
        .then((resp: IGenericResponse) => resolve(resp))
        .catch((err) => reject(err));
    });
  });
}

export async function apiUpdateFundingApplicationLink(
  programId: string | number,
  applicationId: string | number,
): Promise<IGenericResponse> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {programId, applicationId};

    const mutation = `
      mutation UpdateFundingApplicationLink(
        $programId: ID!
        $applicationId: ID!
      ) {
        updateFundingApplicationLink(
          programId: $programId
          applicationId: $applicationId
        ) {
          success
          message
        }
      }
    `;

    return new Promise<IGenericResponse>((resolve, reject) => {
      graphqlRequestHandler(
        'updateFundingApplicationLink', // <- root field
        mutation,
        variables,
        headers,
      )
        .then((resp: IGenericResponse) => resolve(resp))
        .catch((err) => reject(err));
    });
  });
}

export async function apiGetFdLiabilities(): Promise<IGenericType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {};
    return new Promise((resolve, reject) => {
      const query = `
        query{
          getAllFDLiabilityTypes {
              id
              name
          }
        }
      `;
      graphqlRequestHandler('getAllFDLiabilityTypes', query, variables, headers)
        .then((response: IGenericType[]) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpsertFdLiability(
  liabilityData: ISaveLiability,
): Promise<IFdLiability> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {liabilityData};
    const mutation = `
      mutation upsertFDLiability($liabilityData: CreateLiabilityDTO!) {
        upsertFDLiability(liabilityData: $liabilityData) {
          id
          creditLimitAmount
          currentBalanceLimit
          monthlyInstallmentAmount
          inArrears
          lender
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('upsertFDLiability', mutation, variables, headers)
        .then((result: IFdLiability) => resolve(result))
        .catch((error) => reject(error));
    });
  });
}

export async function apiDeleteFdLiability(id: number): Promise<boolean> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {id};
    const mutation = `
      mutation deleteFDLiability($id: Float!) {
        deleteFDLiability(id: $id)
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('deleteFDLiability', mutation, variables, headers)
        .then((result: boolean) => resolve(result))
        .catch((error) => reject(error));
    });
  });
}

export async function apiUpsertFdProductionCost(
  productionCostData: ISaveProductionCost,
): Promise<IFdProductionCost> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {productionCostData};
    const query = `
      mutation variables($productionCostData: CreateProductionCostDTO!) {
        upsertFDProductionCost(productionCostData: $productionCostData) {
          id
          plantingDate
          year
          numberOfHectares
          seed
          fertiliser
          chemical
          notes
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('upsertFDProductionCost', query, variables, headers)
        .then((response: IFdProductionCost) => resolve(response))
        .catch((error) => reject(error));
    });
  });
}

export async function apiGenerateBusinessPlan(
  applicationId: number | string,
): Promise<IAiBusinessPlan> {
  /* 1️⃣  fetch auth-token first */
  const token = await lshGETAUTH();

  /* 2️⃣  GraphQL payload */
  const variables = {applicationId: Number(applicationId)};
  const query = `
    query variables($applicationId: Float!){
      generateAiBusinessPlan(applicationId: $applicationId){
        executiveSummary
        companyOverview{
          owner
          experience
          farmName
          landDetails
          waterRights
        }
        assets{
          description{risks strengths notes}
          content{name value year condition}
        }
        # liabilities{
        #   description{risks strengths notes}
        #   content{name limit balance installment}
        # }
        fundingRequest{
          description{risks strengths notes}
          content{name value}
        }
        productionPlan{
          description{risks strengths notes}
          content{
            crop
            hectares
            plantingDate
            plantPopulationPerHectare
            irrigation
            estimatedYieldTPerHectare
            estimatedPricePerT
          }
        }
        productionCosts{
          description{risks strengths notes}
          content{name value}
        }
        operatingCosts{
          description{risks strengths notes}
          content{name value}
        }
        financialProjections{
          description{risks strengths notes}
          content{revenue costs netProfit}
        }
        team{
          description{risks strengths notes}
          content{name role}
        }
        customers{
          description{risks strengths notes}
          content{name crops}
        }
        suppliers{name crops}
        applicationStrengths
        applicationWeaknesses
        applicationMitigation{
          description
          content{risk mitigation}
        }
        productionHistory{
          description{risks strengths notes}
          content{year crop hectares yield}
        }
        financialHistory{
          description{risks strengths notes}
          content{year revenue costs netProfit}
        }
        conclusion{
          description
          recommendations
        }
      }
    }`;

  /* 3️⃣  make request */
  return graphqlRequestHandler('generateAiBusinessPlan', query, variables, {
    Authorization: token,
  });
}

export async function apiUpdateFdApplicationState(
  applicationId: string | number,
  newState: EApplicationStateType,
): Promise<IFdApplicationType> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    // GraphQL variables
    const variables = {
      applicationId: String(applicationId),
      state: newState,
    };

    // GraphQL mutation
    const query = `
      mutation updateFDApplicationState(
        $applicationId: ID!
        $state: EApplicationStateType!
      ) {
        updateFDApplicationState(
          applicationId: $applicationId
          state: $state
        ) {
          id
          applicationState    
          completedSections
          incompleteSections
          completedSectionsPercentage
          progress
          subProgress
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'updateFDApplicationState',
        query,
        variables,
        headers,
      )
        .then((updated: IFdApplicationType) => resolve(updated))
        .catch(reject);
    });
  });
}

/**
 * Create or update application ownership (assign owner and reporting-to).
 * @param applicationOwnership Ownership payload
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export async function apiCreateOrUpdateFDApplicationOwnership(
  applicationOwnership: ICreateApplicationOwnershipDto,
): Promise<IApplicationOwnershipType> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    // Map to backend expected input field names
    const mapped = {
      id: applicationOwnership.id,
      applicationId: String(applicationOwnership.applicationId),
      ownerId: String(applicationOwnership.ownerId),
      reportingToId: String(applicationOwnership.reportingToId),
      state: applicationOwnership.state,
      notes: applicationOwnership.notes,
      handOver: applicationOwnership.handOver,
    } as any;
    const variables = {applicationOwnership: mapped};
    const query = `
      mutation variables($applicationOwnership: CreateApplicationOwnershipDTO!) {
        createOrUpdateFDApplicationOwnership(
          applicationOwnership: $applicationOwnership
        ) {
          id
          owner { id name surname }
          ownerId
          reportingTo { id name surname }
          reportingToId
          state
          notes
          createdAt
          updatedAt
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createOrUpdateFDApplicationOwnership',
        query,
        variables,
        headers,
      )
        .then((resp: IApplicationOwnershipType) => resolve(resp))
        .catch(reject);
    });
  });
}

/* ────────────────────────────────
   1️⃣  Get ALL available team roles
   ──────────────────────────────── */
export async function apiGetAllFarmTeamRoles(): Promise<IGenericType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const query = `
      query getAllFDFarmTeamRoles {
        getAllFDFarmTeamRoles {
          id
          name
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getAllFDFarmTeamRoles',
        query,
        {}, // no variables
        headers,
      )
        .then((roles: IGenericType[]) => resolve(roles))
        .catch(reject);
    });
  });
}

/* ──────────────────────────────────────────────
   2️⃣  Fetch existing team members for an application
   ────────────────────────────────────────────── */
export async function apiGetFarmTeamsByApplicationId(
  applicationId: string | number,
): Promise<IFarmTeam[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const variables = {id: Number(applicationId)};

    const query = `
      query getFDFarmTeamsByApplicationId($id: Float!) {
        getFDFarmTeamsByApplicationId(id: $id) {
          id
          name
          description
          farmTeamRole {
            id
            name
          }
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getFDFarmTeamsByApplicationId',
        query,
        variables,
        headers,
      )
        .then((team: IFarmTeam[]) => resolve(team))
        .catch(reject);
    });
  });
}

/* ────────────────────────────────────────────────
   3️⃣  Create **or** update a single team-member record
       (Pass an "id" in farmTeamData to update)
   ──────────────────────────────────────────────── */
type CreateFarmTeamDTO = {
  id?: number; // include to update
  name: string;
  description?: string | null;
  farmTeamRoleId: number;
  applicationId: number;
};

export async function apiCreateFdFarmTeam(
  farmTeamData: CreateFarmTeamDTO,
): Promise<{id: number}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const variables = {farmTeamData};

    const mutation = `
      mutation createFDFarmTeam($farmTeamData: CreateFarmTeamDTO!) {
        createFDFarmTeam(farmTeamData: $farmTeamData) {
          id
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('createFDFarmTeam', mutation, variables, headers)
        .then((resp: {id: number}) => resolve(resp))
        .catch(reject);
    });
  });
}

/* ────────────────────────────────
   4️⃣  Delete an existing team member by id
   ──────────────────────────────── */
export async function apiDeleteFdFarmTeam(id: number): Promise<{id: number}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id};

    const mutation = `
      mutation deleteFDFarmTeam($id: Float!) {
        deleteFDFarmTeam(id: $id) {
          id
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('deleteFDFarmTeam', mutation, variables, headers)
        .then((resp: {id: number}) => resolve(resp))
        .catch(reject);
    });
  });
}

/* ────────────────────────────────
   1️⃣  Get ALL available customers
   ──────────────────────────────── */
export async function apiGetAllFdCustomers(): Promise<IGenericType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const query = `
      query getAllFDCustomers {
        getAllFDCustomers {
          id
          name
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getAllFDCustomers',
        query,
        {}, // no variables
        headers,
      )
        .then((list: IGenericType[]) => resolve(list))
        .catch(reject);
    });
  });
}

/* ────────────────────────────────
   2️⃣  Get ALL available crops / products
   ──────────────────────────────── */
export async function apiGetAllFdCrops(
  longTermCrop?: string | null,
): Promise<IGenericType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {longTermCrop: longTermCrop ?? null};

    const query = `
      query getAllFDCrops($longTermCrop: String) {
        getAllFDCrops(longTermCrop: $longTermCrop) {
          id
          name
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getAllFDCrops', query, variables, headers)
        .then((list: IGenericType[]) => resolve(list))
        .catch(reject);
    });
  });
}

/* ──────────────────────────────────────────────
   3️⃣  Fetch existing customer-crop pairs for an application
   ────────────────────────────────────────────── */
export async function apiGetCustomerSuppliersByApplicationId(
  applicationId: string | number,
): Promise<ICustomerSupplier[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const variables = {id: Number(applicationId)};

    const query = `
      query getFDCustomerSuppliersByApplicationId($id: Float!) {
        getFDCustomerSuppliersByApplicationId(id: $id) {
          id
          crop {
            id
            name
          }
          customer {
            id
            name
          }
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getFDCustomerSuppliersByApplicationId',
        query,
        variables,
        headers,
      )
        .then((list: ICustomerSupplier[]) => resolve(list))
        .catch(reject);
    });
  });
}

/* ────────────────────────────────────────────────
   4️⃣  Create **or** update a single customer-crop record
       (Pass an "id" in customerSupplierData to update)
   ──────────────────────────────────────────────── */
type CreateCustomerSupplierDto = {
  id?: number; // include to update, omit to create
  applicationId: number;
  customerId: number;
  cropId: number;
};

export async function apiCreateFdCustomerSupplier(
  customerSupplierData: CreateCustomerSupplierDto,
): Promise<{id: number}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const variables = {customerSupplierData};

    const mutation = `
      mutation createFDCustomerSupplier(
        $customerSupplierData: CreateCustomerSupplierDTO!
      ) {
        createFDCustomerSupplier(customerSupplierData: $customerSupplierData) {
          id
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createFDCustomerSupplier',
        mutation,
        variables,
        headers,
      )
        .then((resp: {id: number}) => resolve(resp))
        .catch(reject);
    });
  });
}

/* ───────────────────────────────────────────────
   1️⃣  Load every supplier (and their products)
   ─────────────────────────────────────────────── */

export async function apiGetAllFdSuppliers(): Promise<
  {id: number; name: string; products: {id: number; name: string}[]}[]
> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const query = `
      query getFDAllSuppliers {
        getFDAllSuppliers {
          id
          name
          products {
            id
            name
          }
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getFDAllSuppliers', query, {}, headers)
        .then(resolve)
        .catch(reject),
    );
  });
}

/* ───────────────────────────────────────────────
   2️⃣  Fetch existing supplier⇢product links for an application
   ─────────────────────────────────────────────── */
export async function apiGetFarmerSuppliersByApplicationId(
  applicationId: string | number,
): Promise<
  {
    id: number;
    supplierList: {id: number; name: string};
    supplierProductList: {id: number; name: string};
  }[]
> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: Number(applicationId)};

    const query = `
      query getFDFarmerSuppliersByApplicationId($id: Float!) {
        getFDFarmerSuppliersByApplicationId(id: $id) {
          id
          supplierList { id name }
          supplierProductList { id name }
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDFarmerSuppliersByApplicationId',
        query,
        variables,
        headers,
      )
        .then(resolve)
        .catch(reject),
    );
  });
}

/* ───────────────────────────────────────────────
   3️⃣  Create **or** update one supplier⇢product link
   ─────────────────────────────────────────────── */
type CreateFarmerSupplierDto = {
  id?: number;
  supplierId: number;
  supplierProductId: number;
  applicationId: number;
};

export async function apiCreateFdFarmerSupplier(
  farmerSupplierData: CreateFarmerSupplierDto,
): Promise<{id: number}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmerSupplierData};

    const mutation = `
      mutation createFDFarmerSupplier($farmerSupplierData: CreateFarmerSupplierDTO!) {
        createFDFarmerSupplier(farmerSupplierData: $farmerSupplierData) { id }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createFDFarmerSupplier',
        mutation,
        variables,
        headers,
      )
        .then(resolve)
        .catch(reject),
    );
  });
}

type IFdCommodityType = {
  id: number;
  name: string;
};

type IFdApplicationHistoryCommodity = {
  id: number;
  commodityType: IFdCommodityType;
};

type ICreateOrUpdateFdApplicationHistoryCommodityDto = {
  applicationId: number;
  commodityTypeId: number;
};

type IFdLivestockCatalogueItem = {
  id: number;
  name: string;
  livestockType?: string | null;
  livestockProduct?: string | null;
};

type IFdApplicationLivestockRecord = {
  id?: number;
  quantity?: number | null;
  notes?: string | null;
  type?: string | null;
  productGrading?: string | null;
  sellOfPeriod?: string | null;
  price?: number | null;
  livestock?: IFdLivestockCatalogueItem | null;
  application?: {
    id: number;
  } | null;
};

type IUpsertFdApplicationLivestockDto = {
  id?: number;
  applicationId: number;
  livestockId: number;
  quantity: number;
  notes?: string;
  type?: string;
  productGrading?: string;
  sellOfPeriod?: string;
  price?: number;
};

type IFdApplicationLongTermCropRecord = {
  id?: number;
  blockNumber?: string | null;
  age?: number | null;
  numberOfTrees?: number | null;
  area?: number | null;
  trellies?: boolean | null;
  spacing?: string | null;
  irrigation?: string | null;
  type?: string | null;
  notes?: string | null;
  projectedYear?: number | null;
  projectedYield?: number | null;
  projectedPrice?: number | null;
  projectedArea?: number | null;
  historyYear1?: number | null;
  historyYield1?: number | null;
  historyArea1?: number | null;
  historyYear2?: number | null;
  historyYield2?: number | null;
  historyArea2?: number | null;
  historyYear3?: number | null;
  historyYield3?: number | null;
  historyArea3?: number | null;
  application?: {
    id: number;
  } | null;
  crop?: {
    id: number;
    name: string;
  } | null;
};

type IUpsertFdApplicationLongTermCropDto = {
  id?: number;
  applicationId: number;
  cropId: number;
  blockNumber: string;
  age: number;
  numberOfTrees: number;
  area: number;
  trellies: boolean;
  spacing: number;
  irrigation: string;
  type: string;
  notes?: string;
  projectedYear: number;
  projectedYield: number;
  projectedPrice: number;
  projectedArea: number;
  historyYear1: number;
  historyYield1: number;
  historyArea1: number;
  historyYear2: number;
  historyYield2: number;
  historyArea2: number;
  historyYear3: number;
  historyYield3: number;
  historyArea3: number;
};

type IFdApplicationWaterRightRecord = {
  id?: number;
  scheme?: string | null;
  schedule?: string | null;
  waterSource?: string[] | null;
  waterRights?: boolean | null;
  waterScale?: string | null;
  permitNumber?: string | null;
  controlled?: boolean | null;
  notes?: string | null;
  householdConsumption?: string[] | null;
  waterQuantity?: number | null;
};

type IUpsertFdApplicationWaterRightDto = {
  id?: number;
  applicationId: number;
  waterRights: boolean;
  waterSource: string[];
  householdConsumption: string[];
  scheme: string;
  permitNumber: string;
  schedule?: string;
  waterScale?: string | null;
  waterQuantity?: number | null;
  controlled: boolean;
  notes?: string;
};

type IFdApplicationPropertyRecord = {
  id?: number;
  nameOfDeed?: string | null;
  areaDistrict?: string | null;
  farmArrangement?: string | null;
  term?: number | null;
  amount?: number | null;
  dryland?: number | null;
  irrigation?: number | null;
  pastures?: number | null;
  grazing?: number | null;
  intensive?: number | null;
  other?: number | null;
  notes?: string | null;
  total?: number | null;
};

type IUpsertFdApplicationPropertyDto = {
  id?: number;
  applicationId: number;
  nameOfDeed: string;
  areaDistrict: string;
  farmArrangement?: string | null;
  term?: number | null;
  amount?: number | null;
  dryland?: number | null;
  irrigation?: number | null;
  pastures?: number | null;
  grazing?: number | null;
  intensive?: number | null;
  other?: number | null;
  notes?: string;
};

export async function apiGetAllFdCommodityTypes(): Promise<IFdCommodityType[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getAllFDCommodityTypes {
        getAllFDCommodityTypes {
          id
          name
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getAllFDCommodityTypes', query, {}, headers)
        .then((rows: IFdCommodityType[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiGetFdApplicationHistoryCommoditiesByApplicationId(
  applicationId: string | number,
): Promise<IFdApplicationHistoryCommodity[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const query = `
      query getFDApplicationHistoryCommoditiesByApplicationId($applicationId: ID!) {
        getFDApplicationHistoryCommoditiesByApplicationId(applicationId: $applicationId) {
          id
          commodityType {
            id
            name
          }
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDApplicationHistoryCommoditiesByApplicationId',
        query,
        variables,
        headers,
      )
        .then((rows: IFdApplicationHistoryCommodity[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiCreateFdApplicationHistoryCommodities(
  applicationHistoryCommodities: ICreateOrUpdateFdApplicationHistoryCommodityDto[],
): Promise<IFdApplicationHistoryCommodity[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {
      applicationHistoryCommodities: (applicationHistoryCommodities || []).map(
        (item) => ({
          applicationId: String(item.applicationId),
          commodityTypeId: String(item.commodityTypeId),
        }),
      ),
    };
    const mutation = `
      mutation createFDApplicationHistoryCommodities(
        $applicationHistoryCommodities: [CreateOrUpdateApplicationHistoryCommodityDTO!]!
      ) {
        createFDApplicationHistoryCommodities(
          applicationHistoryCommodities: $applicationHistoryCommodities
        ) {
          id
          commodityType {
            id
            name
          }
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createFDApplicationHistoryCommodities',
        mutation,
        variables,
        headers,
      )
        .then((rows: IFdApplicationHistoryCommodity[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiGetAllFdLivestock(): Promise<
  IFdLivestockCatalogueItem[]
> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const query = `
      query getAllFDLivestock {
        getAllFDLivestock {
          id
          name
          livestockType
          livestockProduct
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getAllFDLivestock', query, {}, headers)
        .then((rows: IFdLivestockCatalogueItem[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiGetFdApplicationLivestockByApplicationId(
  applicationId: string | number,
): Promise<IFdApplicationLivestockRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const query = `
      query getFDApplicationLivestockByApplicationId($applicationId: ID!) {
        getFDApplicationLivestockByApplicationId(applicationId: $applicationId) {
          id
          quantity
          notes
          type
          productGrading
          sellOfPeriod
          price
          livestock {
            id
            name
            livestockType
            livestockProduct
          }
          application {
            id
          }
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDApplicationLivestockByApplicationId',
        query,
        variables,
        headers,
      )
        .then((rows: IFdApplicationLivestockRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiCreateOrUpdateMultipleFdApplicationLivestock(
  applicationLivestock: IUpsertFdApplicationLivestockDto[],
): Promise<IFdApplicationLivestockRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationLivestock};
    const mutation = `
      mutation createOrUpdateMultipleFDApplicationLivestock(
        $applicationLivestock: [CreateApplicationLivestockDTO!]!
      ) {
        createOrUpdateMultipleFDApplicationLivestock(
          applicationLivestock: $applicationLivestock
        ) {
          id
          quantity
          notes
          type
          productGrading
          sellOfPeriod
          price
          livestock {
            id
            name
            livestockType
            livestockProduct
          }
          application {
            id
          }
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createOrUpdateMultipleFDApplicationLivestock',
        mutation,
        variables,
        headers,
      )
        .then((rows: IFdApplicationLivestockRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiDeleteFdApplicationLivestock(
  id: string | number,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: String(id)};
    const mutation = `
      mutation deleteFDApplicationLivestock($id: ID!) {
        deleteFDApplicationLivestock(id: $id)
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFDApplicationLivestock',
        mutation,
        variables,
        headers,
      )
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiGetFdApplicationLongTermCropsByApplicationId(
  applicationId: string | number,
): Promise<IFdApplicationLongTermCropRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const query = `
      query getFDApplicationLongTermCropsByApplicationId($applicationId: ID!) {
        getFDApplicationLongTermCropsByApplicationId(applicationId: $applicationId) {
          id
          blockNumber
          age
          numberOfTrees
          area
          trellies
          spacing
          irrigation
          type
          notes
          projectedYear
          projectedYield
          projectedPrice
          projectedArea
          historyYear1
          historyYield1
          historyArea1
          historyYear2
          historyYield2
          historyArea2
          historyYear3
          historyYield3
          historyArea3
          application {
            id
          }
          crop {
            id
            name
          }
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDApplicationLongTermCropsByApplicationId',
        query,
        variables,
        headers,
      )
        .then((rows: IFdApplicationLongTermCropRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiCreateOrUpdateFdLongTermCrops(
  applicationLongTermCrops: IUpsertFdApplicationLongTermCropDto[],
): Promise<IFdApplicationLongTermCropRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationLongTermCrops};
    const mutation = `
      mutation createOrUpdateFDLongTermCrops(
        $applicationLongTermCrops: [CreateApplicationLongTermCropDTO!]!
      ) {
        createOrUpdateFDLongTermCrops(
          applicationLongTermCrops: $applicationLongTermCrops
        ) {
          id
          blockNumber
          age
          numberOfTrees
          area
          trellies
          spacing
          irrigation
          type
          notes
          projectedYear
          projectedYield
          projectedPrice
          projectedArea
          historyYear1
          historyYield1
          historyArea1
          historyYear2
          historyYield2
          historyArea2
          historyYear3
          historyYield3
          historyArea3
          application {
            id
          }
          crop {
            id
            name
          }
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createOrUpdateFDLongTermCrops',
        mutation,
        variables,
        headers,
      )
        .then((rows: IFdApplicationLongTermCropRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiDeleteFdApplicationLongTermCrop(
  id: string | number,
  blockNumber: string,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: String(id), blockNumber};
    const mutation = `
      mutation deleteFDApplicationLongTermCrop($id: ID!, $blockNumber: String!) {
        deleteFDApplicationLongTermCrop(id: $id, blockNumber: $blockNumber)
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFDApplicationLongTermCrop',
        mutation,
        variables,
        headers,
      )
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiGetFdApplicationWaterRightsByApplicationId(
  applicationId: string | number,
): Promise<IFdApplicationWaterRightRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const query = `
      query getFDApplicationWaterRightsByApplicationId($applicationId: ID!) {
        getFDApplicationWaterRightsByApplicationId(applicationId: $applicationId) {
          id
          scheme
          schedule
          waterSource
          waterRights
          waterScale
          permitNumber
          controlled
          notes
          householdConsumption
          waterQuantity
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDApplicationWaterRightsByApplicationId',
        query,
        variables,
        headers,
      )
        .then((rows: IFdApplicationWaterRightRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiCreateOrUpdateMultipleFdApplicationWaterRights(
  applicationWaterRights: IUpsertFdApplicationWaterRightDto[],
): Promise<IFdApplicationWaterRightRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationWaterRights};
    const mutation = `
      mutation createOrUpdateMultipleFDApplicationWaterRights(
        $applicationWaterRights: [UpdateApplicationWaterRightsDTO!]!
      ) {
        createOrUpdateMultipleFDApplicationWaterRights(
          applicationWaterRights: $applicationWaterRights
        ) {
          id
          scheme
          schedule
          waterSource
          waterRights
          waterScale
          permitNumber
          controlled
          notes
          householdConsumption
          waterQuantity
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createOrUpdateMultipleFDApplicationWaterRights',
        mutation,
        variables,
        headers,
      )
        .then((rows: IFdApplicationWaterRightRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiDeleteFdApplicationWaterRights(
  id: string | number,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: String(id)};
    const mutation = `
      mutation deleteFDApplicationWaterRights($id: ID!) {
        deleteFDApplicationWaterRights(id: $id)
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFDApplicationWaterRights',
        mutation,
        variables,
        headers,
      )
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiDeleteFdApplicationWaterRightsByApplicationId(
  applicationId: string | number,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const mutation = `
      mutation deleteFDApplicationWaterRightsByApplicationId($applicationId: ID!) {
        deleteFDApplicationWaterRightsByApplicationId(applicationId: $applicationId)
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFDApplicationWaterRightsByApplicationId',
        mutation,
        variables,
        headers,
      )
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiGetFdApplicationPropertiesByApplicationId(
  applicationId: string | number,
): Promise<IFdApplicationPropertyRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationId: String(applicationId)};
    const query = `
      query getFDApplicationProperties($applicationId: ID!) {
        getFDApplicationProperties(applicationId: $applicationId) {
          id
          nameOfDeed
          areaDistrict
          farmArrangement
          term
          amount
          dryland
          irrigation
          pastures
          grazing
          intensive
          other
          notes
          total
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDApplicationProperties',
        query,
        variables,
        headers,
      )
        .then((rows: IFdApplicationPropertyRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiCreateOrUpdateFdApplicationProperties(
  applicationProperties: IUpsertFdApplicationPropertyDto[],
): Promise<IFdApplicationPropertyRecord[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {applicationProperties};
    const mutation = `
      mutation createOrUpdateFDApplicationProperties(
        $applicationProperties: [CreateApplicationPropertyDTO!]!
      ) {
        createOrUpdateFDApplicationProperties(
          applicationProperties: $applicationProperties
        ) {
          id
          nameOfDeed
          areaDistrict
          farmArrangement
          term
          amount
          dryland
          irrigation
          pastures
          grazing
          intensive
          other
          notes
          total
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'createOrUpdateFDApplicationProperties',
        mutation,
        variables,
        headers,
      )
        .then((rows: IFdApplicationPropertyRecord[]) => resolve(rows || []))
        .catch(reject),
    );
  });
}

export async function apiDeleteFdApplicationProperty(
  id: string | number,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: String(id)};
    const mutation = `
      mutation deleteFDApplicationProperty($id: ID!) {
        deleteFDApplicationProperty(id: $id)
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFDApplicationProperty',
        mutation,
        variables,
        headers,
      )
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

/* ------------------------- getAll (static master list) -------------------- */
export async function apiGetAllFarmRisks(): Promise<IFarmRiskBasic[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const query = `
      query {
        getAllFDFarmRisks { id name }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getAllFDFarmRisks', query, {}, headers)
        .then((res) => resolve(res as IFarmRiskBasic[]))
        .catch(reject),
    );
  });
}

/* -------------------- get risks already linked to an app ------------------ */
export async function apiGetFarmRisksByApplicationId(
  applicationId: number | string,
): Promise<IFarmRiskByApp[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: Number(applicationId)};

    const query = `
      query getFDFarmRisksByApplicationId($id: Float!) {
        getFDFarmRisksByApplicationId(id: $id) {
          id
          mitigation
          problem
          risk { id name }
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'getFDFarmRisksByApplicationId',
        query,
        variables,
        headers,
      )
        .then((res) => resolve(res as IFarmRiskByApp[]))
        .catch(reject),
    );
  });
}

/* ------------------------- satellite risks widget ------------------------- */
export interface ISatelliteRisk {
  applicationId: number;
  farmerName: string;
  farmName: string;
  riskCount: number;
}

export async function apiGetSatelliteRisks(
  crmUserId: number,
): Promise<ISatelliteRisk[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {crmUserId};

    const query = `
      query getSatelliteRisks($crmUserId: Int!) {
        getSatelliteRisks(crmUserId: $crmUserId) {
          applicationId
          farmerName
          farmName
          riskCount
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getSatelliteRisks', query, variables, headers)
        .then((res) => resolve(res as ISatelliteRisk[]))
        .catch(reject),
    );
  });
}

/* ------------------------- create / update single risk -------------------- */
export async function apiCreateFdFarmRisk(
  farmRiskData: ICreateFarmRiskDto,
): Promise<{id: number}> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmRiskData};

    const mutation = `
      mutation createFDFarmRisk($farmRiskData: CreateFarmRiskDTO!) {
        createFDFarmRisk(farmRiskData: $farmRiskData) { id }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler('createFDFarmRisk', mutation, variables, headers)
        .then(resolve)
        .catch(reject),
    );
  });
}

export async function apiDeleteFdFarmRisk(
  id: number | string,
): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: Number(id)};

    const mutation = `
      mutation deleteFDFarmRisk($id: Float!) {
        deleteFDFarmRisk(id: $id)
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler('deleteFDFarmRisk', mutation, variables, headers)
        .then((result: boolean) => resolve(Boolean(result)))
        .catch(reject),
    );
  });
}

export async function apiRunItcTraderApplication(
  data: IRunItcTraderApplicationDto,
): Promise<IitcDoc> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = data;

    const mutation = `
      mutation runItcTraderApplication(
        $name: String!
        $surname: String!
        $idNumber: String!
        $externalServiceRequest: Boolean!
      ) {
        runItcTraderApplication(
          name: $name
          surname: $surname
          idNumber: $idNumber
          externalServiceRequest: $externalServiceRequest
        ) {
          personName
          personSurname
          finalDocLink
          rawDataLink
          requester { name }
          requestedOn
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler(
        'runItcTraderApplication',
        mutation,
        variables,
        headers,
      )
        .then(resolve)
        .catch(reject),
    );
  });
}

export async function apiPerformCompanyCreditCheck(
  payload: IPerformCompanyCreditCheckDto,
): Promise<ICompanyItcDoc> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {...payload};

    const mutation = `
      query performCompanyCreditCheck(
        $regNumber1: String!
        $regNumber2: String!
        $regNumber3: String!
        $refresh: Boolean!
      ) {
        performCompanyCreditCheck(
          regNumber1: $regNumber1
          regNumber2: $regNumber2
          regNumber3: $regNumber3
          refresh: $refresh
        ) {
          id
          companyName
          companyRegNumber
          finalDocLink
          rawDataLink
          requester { name }
        }
      }
    `;

    return new Promise<ICompanyItcDoc>((resolve, reject) =>
      graphqlRequestHandler(
        'performCompanyCreditCheck',
        mutation,
        variables,
        headers,
      )
        .then((res) => resolve(res as ICompanyItcDoc))
        .catch(reject),
    );
  });
}

/* -----------------------------------------------------------
 * 1.  Get funding-ask by application-id
 * --------------------------------------------------------- */
export async function apiGetFdFundingAsksByApplicationId(
  applicationId: number,
): Promise<IFdFundingAsk | undefined> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id: applicationId};

    const query = `
      query getFDFundingAsksByApplicationId($id: Float!) {
        getFDFundingAsksByApplicationId(id: $id) {
          id
          fundingRequestCosts { id label cost }
          notes
          expectedFundingYear
          expectedFundingMonth
        }
      }
    `;

    return new Promise<IFdFundingAsk | undefined>((resolve, reject) =>
      graphqlRequestHandler(
        'getFDFundingAsksByApplicationId',
        query,
        variables,
        headers,
      )
        .then((res) => resolve(res as IFdFundingAsk))
        .catch(reject),
    );
  });
}

/* -----------------------------------------------------------
 * 2.  Create / update a funding-ask
 *     (Back-end mutation name = createFDFundingAsk)
 * --------------------------------------------------------- */
export async function apiCreateFdFundingAsk(payload: {
  applicationId: number;
  notes?: string;
  expectedFundingYear?: number;
  expectedFundingMonth?: number;
  fundingRequestCosts: IFdFundingRequestCost[];
}): Promise<IFdFundingAsk> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {data: payload};

    const mutation = `
      mutation createFDFundingAsk($data: CreateFundingAskDTO!) {
        createFDFundingAsk(data: $data) {
          id
          fundingRequestCosts { id label cost }
          notes
          expectedFundingYear
          expectedFundingMonth
        }
      }
    `;

    return new Promise<IFdFundingAsk>((resolve, reject) =>
      graphqlRequestHandler('createFDFundingAsk', mutation, variables, headers)
        .then((res) => resolve(res as IFdFundingAsk))
        .catch(reject),
    );
  });
}

/* ──────────────────────────────────────────────────────────────
   Delete a farmer-supplier link
   ────────────────────────────────────────────────────────────── */
export async function apiDeleteFarmerSupplier(payload: {
  applicationId: number;
  supplierId: number;
  supplierProductId: number;
}): Promise<boolean> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {...payload};

    const mutation = `
      mutation deleteFarmerSupplier(
        $applicationId: Float!
        $supplierId: Float!
        $supplierProductId: Float!
      ) {
        deleteFarmerSupplier(
          applicationId: $applicationId
          supplierId: $supplierId
          supplierProductId: $supplierProductId
        )
      }
    `;

    return new Promise<boolean>((resolve, reject) =>
      graphqlRequestHandler(
        'deleteFarmerSupplier',
        mutation,
        variables,
        headers,
      )
        .then((res) => resolve(res as boolean))
        .catch(reject),
    );
  });
}

/* ──────────────────────────────────────────────────────────────
   Delete a customer-supplier link
   ────────────────────────────────────────────────────────────── */
export async function apiDeleteCustomerSupplier(payload: {
  applicationId: number;
  customerId: number;
  cropId: number;
}): Promise<boolean> {
  const headers = {Authorization: await lshGETAUTH()};

  /* --- make 100 % sure everything is numeric --- */
  const variables = {
    applicationId: Number(payload.applicationId),
    customerId: Number(payload.customerId),
    cropId: Number(payload.cropId),
  };

  const mutation = `
    mutation deleteCustomerSupplier(
      $applicationId: Float!
      $customerId: Float!
      $cropId: Float!
    ) {
      deleteCustomerSupplier(
        applicationId: $applicationId
        customerId: $customerId
        cropId: $cropId
      )
    }
  `;

  return new Promise<boolean>((resolve, reject) =>
    graphqlRequestHandler(
      'deleteCustomerSupplier',
      mutation,
      variables,
      headers,
    )
      .then((res) => resolve(Boolean(res)))
      .catch(reject),
  );
}

export async function apiGetWidgets(
  limit?: number,
  offset?: number,
): Promise<IWidget[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {limit, offset};
    const query = `
      query variables($limit: Int, $offset: Int) {
        supplierWidgets(limit: $limit, offset: $offset) {
          id
          supplierId
          name
          x
          y
          width
          height
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('supplierWidgets', query, variables, headers)
        .then(resolve)
        .catch(reject);
    });
  });
}

export async function apiReplaceSupplierWidgets(
  inputs: IWidgetInput[],
): Promise<IWidget[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input: inputs};
    const mutation = `
      mutation variables($input: [SupplierWidgetInput!]!) {
        supplierWidgetsReplaceAll(input: $input) {
          id
          supplierId
          name
          x
          y
          width
          height
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'supplierWidgetsReplaceAll',
        mutation,
        variables,
        headers,
      )
        .then(resolve)
        .catch(reject);
    });
  });
}

// ----------------------------------------------------------------------
// DATA POINT MUTATIONS
// ----------------------------------------------------------------------

export async function apiCreateFarmNote(
  input: {
    comment: string;
    noteTypeId: number | string; // Required by backend
    farmFieldId?: number | string | null;
    lat?: number | null;
    long?: number | null;
  },
  farmFieldId: number | string | null,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const variables = {
      input,
      farmFieldId,
    };

    const mutation = `
      mutation CreateFarmNote($input: FarmNoteInput!, $farmFieldId: ID!) {
        createFarmNote(input: $input, farmFieldId: $farmFieldId) {
          id
          createdAt
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('createFarmNote', mutation, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreateSoilAssessment(input: {
  farmFieldId: number | string | null;
  noteId?: number | string | null;
  soilType?: string;
  soilStructure?: string;
  hardpanPresence?: boolean;
  organicMatterPct?: number;
  pH?: number;
  P?: number;
  K?: number;
  Ca?: number;
  Na?: number;
  Mg?: number;
  Zn?: number;
  Mn?: number;
  Cu?: number;
  exchangeableAcidity?: number;
  totalCations?: number;
  soilAmendmentsNeeded?: string;
  suggestedCropVarieties?: string;
  pestWeedStrategies?: string;
}): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreateSoilAssessment($input: SoilAssessmentInput!) {
        createSoilAssessment(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createSoilAssessment',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreateIssueLog(input: {
  farmFieldId: number | string | null;
  noteId?: number | string | null;
  pestDiseaseId?: number | string | null;
  pestsAndDiseasesId?: number | string | null;
  issueDate: string;
  issueType: string;
  severityScale: number;
  controlMeasuresApplied: string;
  preventativeMeasuresTaken?: string;
  issueDuringHarvest?: boolean;
}): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreateIssueLog($input: IssueLogInput!) {
        createIssueLog(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('createIssueLog', mutation, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreateManagementLog(input: {
  farmFieldId: number | string | null;
  noteId?: number | string | null;
  logDate: string;
  activityType: string;
  fertilizerType?: string;
  applicationRateKgha?: number;
  irrigationFrequency?: string;
  irrigationVolume?: number;
  waterStressSigns?: string;
  initialTreatmentActions?: string;
}): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreateManagementLog($input: ManagementLogInput!) {
        createManagementLog(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('createManagementLog', mutation, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreateHarvestSummary(input: {
  farmFieldId: number | string | null;
  noteId?: number | string | null;
  totalHarvestedQuantityKg?: number;
  avgYieldPerHectareKgha?: number;
  harvestMethod?: string;
  pctFirstGradeProduce?: number;
  commonQualityIssues?: string;
  harvestEfficiencyScale?: number;
  laborersUsed?: number;
  equipmentUsed?: string;
  initialProcessingSteps?: string;
  storageConditions?: string;
  transportationArrangements?: string;
}): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreateHarvestSummary($input: HarvestSummaryInput!) {
        createHarvestSummary(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createHarvestSummary',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreatePostHarvestReview(input: {
  farmFieldId: number | string | null;
  noteId?: number | string | null;
  comparisonWithExpectedYields?: string;
  pestDiseaseEffectiveness?: string;
  nutrientMgmtEffectiveness?: string;
  adjustmentsMadeDuringSeason?: string;
  impactOnYieldQuality?: string;
  marketPricesAchieved?: string;
  laborEfficiencyChallenges?: string;
  equipmentPerformanceIssues?: string;
  costOfProductionAnalysis?: string;
  profitabilityAssessment?: string;
  keySuccesses?: string;
  areasForImprovement?: string;
  recommendationsForNextSeason?: string;
  lessonsLearned?: string;
}): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreatePostHarvestReview($input: PostHarvestReviewInput!) {
        createPostHarvestReview(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createPostHarvestReview',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreateGrowthObservation(input: any): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreateGrowthObservation($input: GrowthObservationInput!) {
        createGrowthObservation(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createGrowthObservation',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiUpdateGrowthObservation(
  id: string | number,
  input: any,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id, input};
    const mutation = `
      mutation UpdateGrowthObservation($id: ID!, $input: GrowthObservationInput!) {
        updateGrowthObservation(growthObservationId: $id, input: $input) {
          id
          updatedAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'updateGrowthObservation',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiUpdateSoilAssessment(
  id: number | string,
  input: {
    farmFieldId?: number | string | null;
    soilStructure?: string;
    hardpanPresence?: boolean;
    organicMatterPct?: number;
    pH?: number;
    P?: number;
    K?: number;
    Ca?: number;
    Na?: number;
    Mg?: number;
    Zn?: number;
    Mn?: number;
    Cu?: number;
    exchangeableAcidity?: number;
    totalCations?: number;
    soilAmendmentsNeeded?: string;
    suggestedCropVarieties?: string;
    pestWeedStrategies?: string;
  },
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id, input};
    const mutation = `
      mutation UpdateSoilAssessment($id: ID!, $input: SoilAssessmentInput!) {
        updateSoilAssessment(id: $id, input: $input) {
          id
          updatedAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'updateSoilAssessment',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiUpdateIssueLog(
  id: number | string,
  input: {
    pestDiseaseId?: number | string | null;
    pestsAndDiseasesId?: number | string | null;
    issueDate?: string;
    issueType?: string;
    severityScale?: number;
    controlMeasuresApplied?: string;
    preventativeMeasuresTaken?: string;
    issueDuringHarvest?: boolean;
  },
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id, input};
    const mutation = `
      mutation UpdateIssueLog($id: ID!, $input: IssueLogInput!) {
        updateIssueLog(issueLogId: $id, input: $input) {
          id
          updatedAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('updateIssueLog', mutation, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

// ----------------------------------------------------------------------
// DATA POINT FETCH QUERIES
// ----------------------------------------------------------------------

const SOIL_ASSESSMENT_SELECTION = `
  id
  noteId
  soilType
  soilStructure
  hardpanPresence
  organicMatterPct
  pH
  P
  K
  Ca
  Mg
  Na
  Zn
  Mn
  Cu
  exchangeableAcidity
  totalCations
  anions
  soilAmendmentsNeeded
  suggestedCropVarieties
  pestWeedStrategies
  createdAt
`;

export async function apiGetSoilAssessments(
  farmFieldId: number | string | null,
): Promise<any[]> {
  if (farmFieldId === null || farmFieldId === undefined || farmFieldId === '') {
    return [];
  }

  try {
    return await lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const variables = {farmFieldId};
      const query = `
        query GetSoilAssessments($farmFieldId: ID!) {
          soilAssessments(farmFieldId: $farmFieldId) {
            ${SOIL_ASSESSMENT_SELECTION}
          }
        }
      `;
      return new Promise<any[]>((resolve, reject) => {
        graphqlRequestHandler('soilAssessments', query, variables, headers)
          .then((data) => resolve(Array.isArray(data) ? data : []))
          .catch(reject);
      });
    });
  } catch (error) {
    try {
      const latest = await apiGetLatestSoilAssessment(farmFieldId);
      return latest ? [latest] : [];
    } catch (fallbackError) {
      console.error(
        'Failed to fetch soil assessments (list + latest fallback)',
        error,
        fallbackError,
      );
      return [];
    }
  }
}

export async function apiGetLatestSoilAssessment(
  farmFieldId: number | string | null,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestSoilAssessment($farmFieldId: ID!) {
        latestSoilAssessment(farmFieldId: $farmFieldId) {
          id
          soilType
          soilStructure
          hardpanPresence
          organicMatterPct
          pH
          P
          K
          Ca
          Mg
          Na
          Zn
          Mn
          Cu
          exchangeableAcidity
          totalCations
          anions
          soilAmendmentsNeeded
          suggestedCropVarieties
          pestWeedStrategies
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('latestSoilAssessment', query, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetLatestIssueLog(
  farmFieldId: number | string | null,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestIssueLog($farmFieldId: ID!) {
        latestIssueLog(farmFieldId: $farmFieldId) {
          id
          issueDate
          issueType
          severityScale
          controlMeasuresApplied
          preventativeMeasuresTaken
          issueDuringHarvest
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('latestIssueLog', query, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetFarmNotes(
  farmFieldId: number | string | null,
): Promise<any[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetFarmNotes($farmFieldId: ID!) {
        farmNotes(farmFieldId: $farmFieldId) {
          id
          comment
          lat
          long
          imageUrl
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('farmNotes', query, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

const FARM_FIELD_LOGS_SELECTION = `
  id
  info {
    hectares
    fieldName
    variety
    plantedOn
    plantPopulation
    irrigation
    expectedYield
    produce {
      id
      name
    }
  }
  notes {
    id
    noteDate
    noteText
    title
    comment
    lat
    long
    isCritical
    createdAt
    updatedAt
    images {
      id
      imageUrl
      caption
    }
  }
  growthObservations {
    id
    noteId
    observationDate
    cropGrowthStage
    averagePlantHeightCm
    leafAreaIndex
    overallCropHealthRating
    pestAndDiseasePresence
    weedPressure
    floweringIntensity
    fruitSetIntensity
    estimatedYieldKgha
    germinationDate
    expectedGerminationRate
    estimatedYield
    yieldEstimate
    plantHeightCm
    cropMaturityAssessment
    germinationRatePct
    seedlingVigorScale
    rootLengthScale
    uniformityOfMaturityScale
    leavesPerPlant
    floweringUniformityScale
    fruitSetPercent
    fruitSetUniformityScale
    fruitAvgLengthCm
    pctCropReadyForHarvest
    createdAt
    updatedAt
  }
  issueLogs {
    id
    noteId
    issueDate
    issueType
    description
    severityScale
    affectedAreaHectares
    controlMeasuresApplied
    preventativeMeasuresTaken
    curativeMeasuresTaken
    issueStatus
    issueDuringHarvest
    createdAt
    updatedAt
  }
  managementLogs {
    id
    noteId
    logDate
    activityType
    inputsUsed
    fertilizerType
    applicationRateKgha
    laborHours
    machineryUsed
    irrigationFrequency
    irrigationVolume
    weatherConditions
    waterStressSigns
    initialTreatmentActions
    createdAt
    updatedAt
  }
  harvestSummaries {
    id
    noteId
    harvestDate
    totalHarvestedQuantityKg
    avgYieldPerHectareKgha
    harvestMethod
    pctFirstGradeProduce
    commonQualityIssues
    harvestEfficiencyScale
    qualityGradesDistribution
    laborersUsed
    equipmentUsed
    initialProcessingSteps
    storageConditions
    transportationArrangements
    createdAt
    updatedAt
  }
  postHarvestReviews {
    id
    noteId
    reviewDate
    marketPricesAchieved
    costOfProductionAnalysis
    profitabilityAssessment
    laborEfficiencyChallenges
    equipmentPerformanceIssues
    recommendationsForNextSeason
    areasForImprovement
    keySuccesses
    comparisonWithExpectedYields
    pestDiseaseEffectiveness
    nutrientMgmtEffectiveness
    adjustmentsMadeDuringSeason
    impactOnYieldQuality
    lessonsLearned
    createdAt
    updatedAt
  }
`;

export async function apiGetFarmFieldWithLogs(
  fieldId: number | string,
): Promise<IFarmFieldLogData | null> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {fieldId};
    const query = `
      query GetFarmFieldWithLogs($fieldId: ID!) {
        getFarmField(fieldId: $fieldId) {
          ${FARM_FIELD_LOGS_SELECTION}
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getFarmField', query, variables, headers)
        .then((data) => resolve((data as IFarmFieldLogData) ?? null))
        .catch(reject);
    });
  });
}

export async function apiGetFarmFieldsWithLogs(
  farmId: number | string,
): Promise<IFarmFieldLogData[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmId};
    const query = `
      query GetFarmFieldsWithLogs($farmId: ID!) {
        getFarmFields(farmId: $farmId) {
          ${FARM_FIELD_LOGS_SELECTION}
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getFarmFields', query, variables, headers)
        .then((data) => resolve((data as IFarmFieldLogData[]) ?? []))
        .catch(reject);
    });
  });
}

export async function apiGetLatestGrowthObservation(
  farmFieldId: number | string | null,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestGrowthObservation($farmFieldId: ID!) {
        latestGrowthObservation(farmFieldId: $farmFieldId) {
          id
          stage
          observationDate
          latestAcceptablePlantingDate
          expectedGerminationRate
          estimatedYield
          plannedHa
          haPrepared
          rowSpacing
          plantedPct
          plantHeightCm
          germinationRatePct
          seedlingVigorScale
          rootLengthScale
          cropMaturityAssessment
          uniformityOfMaturity
          leavesPerPlant
          floweringUniformityScale
          marketReady
          marketReadyComment
          tonsHedged
          fruitSetPercent
          fruitSetUniformityScale
          fruitAvgLengthCm
          fruitAvgDiameterCm
          pctCropReadyForHarvest
          estimatedHarvestDate
          logisticsReady
          comment
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'latestGrowthObservation',
        query,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetLatestManagementLog(
  farmFieldId: number | string,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestManagementLog($farmFieldId: ID!) {
        latestManagementLog(farmFieldId: $farmFieldId) {
          id
          logDate
          activityType
          fertilizerType
          applicationRateKgha
          irrigationFrequency
          irrigationVolume
          waterStressSigns
          initialTreatmentActions
          comment
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('latestManagementLog', query, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetLatestHarvestSummary(
  farmFieldId: number | string,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestHarvestSummary($farmFieldId: ID!) {
        latestHarvestSummary(farmFieldId: $farmFieldId) {
          id
          totalHarvestedQuantityKg
          avgYieldPerHectareKgha
          harvestMethod
          pctFirstGradeProduce
          commonQualityIssues
          harvestEfficiencyScale
          laborersUsed
          equipmentUsed
          initialProcessingSteps
          storageConditions
          transportationArrangements
          comment
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('latestHarvestSummary', query, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetLatestPostHarvestReview(
  farmFieldId: number | string,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestPostHarvestReview($farmFieldId: ID!) {
        latestPostHarvestReview(farmFieldId: $farmFieldId) {
          id
          comparisonWithExpectedYields
          pestDiseaseEffectiveness
          nutrientMgmtEffectiveness
          adjustmentsMadeDuringSeason
          impactOnYieldQuality
          marketPricesAchieved
          laborEfficiencyChallenges
          equipmentPerformanceIssues
          costOfProductionAnalysis
          profitabilityAssessment
          keySuccesses
          areasForImprovement
          recommendationsForNextSeason
          lessonsLearned
          comment
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'latestPostHarvestReview',
        query,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

/* -------------------- funding per province per crop widget -------------------- */
export interface IFdFundingPerProvincePerCrop {
  province: string;
  crop: string;
  totalFunding: number;
  applicationCount: number;
}

export async function apiGetFundingPerProvincePerCrop(): Promise<
  IFdFundingPerProvincePerCrop[]
> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const query = `
      query getFundingPerProvincePerCrop {
        getFundingPerProvincePerCrop {
          province
          crop
          totalFunding
          applicationCount
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getFundingPerProvincePerCrop', query, {}, headers)
        .then((res) => resolve(res as IFdFundingPerProvincePerCrop[]))
        .catch(reject),
    );
  });
}

export async function apiGetFdFinancialRatios(
  applicationId: number | string,
): Promise<import('./interfaces').IFdFinancialRatios> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {applicationId: Number(applicationId)};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($applicationId: Int!){
            getFDFinancialRatios(applicationId: $applicationId){
              applicationId
              ltv
              dscr
              grossMargin
              netProfitMargin
              debtToEquity
              interestCover
            }
          }`;
      graphqlRequestHandler('getFDFinancialRatios', query, variables, headers)
        .then((ratios) =>
          resolve(ratios as import('./interfaces').IFdFinancialRatios),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetFdApplicationAuditLogs(
  limit: number = 20,
): Promise<import('./interfaces').IFdApplicationAuditLog[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {limit};
    return new Promise((resolve, reject) => {
      const query = `
          query variables($limit: Int){
            getFDApplicationAuditLogs(limit: $limit){
              id
              applicationId
              application {
                id
                farm {
                  name
                }
                company {
                  name
                }
                applyingUser {
                  name
                  surname
                }
              }
              changedBy
              field
              fromValue
              toValue
              changedAt
            }
          }`;
      graphqlRequestHandler(
        'getFDApplicationAuditLogs',
        query,
        variables,
        headers,
      )
        .then((logs) =>
          resolve(logs as import('./interfaces').IFdApplicationAuditLog[]),
        )
        .catch((error) => reject(error));
    });
  });
}

export async function apiGetTopInputProducts(
  brandSupplierId?: string | number,
  startDate?: string,
  endDate?: string,
  limit: number = 4,
): Promise<import('./interfaces').ITopInputProduct[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {brandSupplierId, startDate, endDate, limit};
    return new Promise((resolve, reject) => {
      const query = `
        query variables(
          $brandSupplierId: ID,
          $startDate: String,
          $endDate: String,
          $limit: Int
        ) {
          topInputProducts(
            brandSupplierId: $brandSupplierId,
            startDate: $startDate,
            endDate: $endDate,
            limit: $limit
          ) {
            productId
            stockCode
            productName
            totalUnits
          }
        }`;
      graphqlRequestHandler('topInputProducts', query, variables, headers)
        .then((res) =>
          resolve(res as import('./interfaces').ITopInputProduct[]),
        )
        .catch((error) => reject(error));
    });
  });
}

// Groups API moved to constants/api/groups.ts — re-exported here so existing
// `import {...} from 'constants/Api'` call sites keep working unchanged.
export * from 'constants/api/groups';

export async function apiGETRESTRICTEDACCOUNTS(
  subCategories: number[],
  farmerId: string,
): Promise<any> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        query variables($subCategories: [ID], $farmerId: ID) {
          getRestrictedAccounts(subCategories: $subCategories, farmerId: $farmerId) {
            account {
              id
              accountType
              accountNumber
            }
            restictionAmount
            subCategory
          }
        }
      `;

      const variables = {
        subCategories,
        farmerId,
      };

      graphqlRequestHandler('getRestrictedAccounts', query, variables, headers)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: any) => reject(error));
    });
  });
}

export async function apiGetLegacyRestrictedAccounts(
  subCategories: number[],
  farmerId: string,
): Promise<any> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        query variables($subCategories: [ID], $farmerId: ID) {
          getLegacyRestrictedAccounts(subCategories: $subCategories, farmerId: $farmerId) {
            account {
              id
              accountType
              accountNumber
            }
            restictionAmount
            subCategory
          }
        }
      `;

      const variables = {
        subCategories,
        farmerId,
      };

      graphqlRequestHandler(
        'getLegacyRestrictedAccounts',
        query,
        variables,
        headers,
      )
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: any) => reject(error));
    });
  });
}
